import { useEffect, useLayoutEffect, useState, useContext, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { db, auth, functions } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { Capacitor } from '@capacitor/core';
import { openCheckout, openExternalUrl } from '../services/nativeBrowser';
import {
  doc,
  getDoc,
  updateDoc,
  addDoc,
  collection,
  serverTimestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import imageCompression from 'browser-image-compression';
import { UserContext } from '../contexts/UserContext';
import FeedbackForm from '../components/FeedbackForm';
import AddressRequiredModal from '../components/AddressRequiredModal';
import ImageOverlay from '../components/ImageOverlay';
import { useContact } from '../contexts/ContactContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { getTradeReceiverPayoutCents } from '../utils/tradePayoutDisplay';
import { getPayoutReleaseStatus } from '../utils/escrowDisplay';
import PayoutReleaseBadge from '../components/PayoutReleaseBadge';
import ShippingPricesModal, { ShippingPricesLink } from '../components/ShippingPricesModal';
import { placeholderFor } from '../utils/listingPlaceholder';

const TradeProgress = () => {
  const { id } = useParams();
  const { notifications, markAsRead } = useNotifications();

  // Mark trade progress notifications as read when viewing this trade
  useEffect(() => {
    const markTradeProgressNotificationsRead = async () => {
      const tradeProgressNotifications = notifications.filter(
        n => (n.type === 'ready_to_ship' || n.type === 'tracking_added_trade' ||
              n.type === 'package_delivered_trade' || n.type === 'trade_complete' ||
              n.type === 'step_reminder') &&
             n.tradeId === id && 
             !n.read
      );
      
      if (tradeProgressNotifications.length > 0) {
        console.log(`📧 Marking ${tradeProgressNotifications.length} trade progress notification(s) as read`);
        for (const notification of tradeProgressNotifications) {
          await markAsRead(notification.id);
        }
      }
    };

    if (notifications.length > 0 && id) {
      markTradeProgressNotificationsRead();
    }
  }, [notifications, markAsRead, id]);
  const [searchParams] = useSearchParams();
  const { openContact } = useContact();
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  // Distinguish "still resolving auth / fetching" from "genuinely missing" so we
  // never flash "Trade not found" during load or pull-to-refresh (full reload).
  const [authChecked, setAuthChecked] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [usernames, setUsernames] = useState({ userA: 'User A', userB: 'User B' });
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentSuccessType, setPaymentSuccessType] = useState('shipping');
  
  // Shipping label state
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [showShippingPrices, setShowShippingPrices] = useState(false);
  const [packageData, setPackageData] = useState({
    length: '',
    width: '',
    height: '',
    weight: '',
    fromAddress: {
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      email: '',
      phone: ''
    },
    toAddress: {
      name: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      email: '',
      phone: ''
    }
  });
  const [shippingRates, setShippingRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [markingReceived, setMarkingReceived] = useState(null);
  const [photoOverlay, setPhotoOverlay] = useState({ isOpen: false, url: '', alt: '', photos: [], index: 0 });
  const { shippingAddress, firestoreUser, loading: userLoading, refreshUserData } = useContext(UserContext);
  const [showAddressModal, setShowAddressModal] = useState(false);

  // Auto-open the inline address modal when the user lands here without a
  // complete address, replacing the old yellow "Go to Profile" banner link's
  // navigate-away flow — the last survivor of that pattern (found during
  // Android testing, 2026-08-20). The banner itself stays as a visible cue.
  useEffect(() => {
    if (!userLoading && !isAddressComplete(shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, userLoading]);
  const [myPedal, setMyPedal] = useState(null);
  const [theirPedal, setTheirPedal] = useState(null);
  const [initiatorPedals, setInitiatorPedals] = useState([]);
  
  const maskEmail = (email) => {
    if (!email) return '';
    const dotIndex = email.lastIndexOf('.');
    if (dotIndex === -1) return '*****';
    return '*****@*****' + email.slice(dotIndex);
  };

  const maskPhone = (phone) => {
    if (!phone) return '';
    return '(***) ***-****';
  };

  // Phone number formatting helper - formats for display but keeps original for backend
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    
    // If it already has formatting, return as is
    if (value.includes('(') || value.includes('-')) return value;
    
    // Remove all non-digit characters
    let cleaned = value.replace(/\D/g, '');
    
    // Remove leading "1" if it's there (US country code)  
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      cleaned = cleaned.slice(1);
    }
    
    // Limit to 10 digits
    cleaned = cleaned.slice(0, 10);
    
    // Apply formatting based on length
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
  };
  
  // Collapsible state for completed steps
  const [collapsedSteps, setCollapsedSteps] = useState({});
  const [hasInitializedCollapse, setHasInitializedCollapse] = useState(false);
  
  // Toggle collapse state for a step
  const toggleStepCollapse = (stepIndex) => {
    setCollapsedSteps(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  // Get the pedal the current user received (used by share functions)
  const getReceivedPedal = () => {
    if (!trade || !myPedal || !theirPedal) return null;
    const isFromUser = currentUser?.uid === trade.fromUserId;
    return isFromUser ? theirPedal : myPedal;
  };

  // 📢 Share NPD (New Pedal Day) to Reddit
  const handleShareToReddit = () => {
    const pedalTheyReceived = getReceivedPedal();
    if (!pedalTheyReceived) {
      console.error('No received pedal data');
      return;
    }
    
    const pedalName = `${pedalTheyReceived.make || ''} ${pedalTheyReceived.model || ''}`.trim() || 'Pedal';
    const title = `NPD: ${pedalName}`;
    const postBody = `Just completed a trade on PedalxChange.com and got this ${pedalName}!\n\nCheck out PedalxChange for trading guitar pedals: https://pedalxchange.com`;
    
    // Copy body text to clipboard as fallback
    navigator.clipboard.writeText(postBody).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your post body has been copied to your clipboard! You can paste it into the text field on Reddit.');
    
    const redditUrl = `https://www.reddit.com/r/guitarpedals/submit?type=self&title=${encodeURIComponent(title)}&selftext=${encodeURIComponent(postBody)}`;
    const newWindow = window.open(redditUrl, '_blank');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = redditUrl;
    }
  };

  // 📢 Share NPD to Facebook
  const handleShareToFacebook = () => {
    const pedalTheyReceived = getReceivedPedal();
    if (!pedalTheyReceived) {
      console.error('No received pedal data');
      return;
    }
    
    const pedalName = `${pedalTheyReceived.make || ''} ${pedalTheyReceived.model || ''}`.trim() || 'Pedal';
    const postText = `NPD: ${pedalName}! Just completed a trade on PedalxChange.com and got this ${pedalName}!`;
    const shareUrl = 'https://pedalxchange.com';
    
    // Copy text to clipboard since Facebook doesn't allow pre-filling post text
    navigator.clipboard.writeText(postText).catch(err => {
      console.error('Failed to copy to clipboard:', err);
    });
    
    alert('Your "New Pedal Day" message has been copied to your clipboard! You can paste it into your Facebook post.');
    
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    const newWindow = window.open(fbUrl, '_blank', 'width=600,height=400');
    
    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      window.location.href = fbUrl;
    }
  };
  
  // Initialize collapsed state for completed steps
  useEffect(() => {
    if (!trade) return;
    
    // Define steps locally in useEffect to avoid dependency issues
    const steps = [
      {
        label: 'Completed Payment',
        check: (u) => u.paid || u.payoutReceived,
      },
      {
        label: 'Upload Confirmation Photo',
        check: (u) => u.photoUploaded,
      },
      {
        label: 'Ready to Ship',
        check: (u) => u.ready,
        action: true,
      },
      {
        label: 'Generate Shipping Label',
        check: (u) => u.label,
      },
      {
        // Completes on delivery (carrier or manual "Mark as Received"): the
        // former separate "Marked as Delivered" step folded in here, 2026-11-01.
        label: 'Item In Transit',
        check: (u) => u.delivered,
      },
      {
        label: 'Feedback Provided',
        check: () => {
          if (!trade) return false;
          const isFromUser = currentUser?.uid === trade.fromUserId;
          const feedbackField = isFromUser ? 'fromUserFeedback' : 'toUserFeedback';
          return !!trade[feedbackField];
        },
      },
    ];

    // Determine who receives the payout based on trade type  
    const transferSuccessful = trade.transferStatus?.status === 'success';
    
    let receiverUserId = null;
    
    // Prefer cashFlow fields (set during counter offers) for accuracy
    if (trade.cashFlow && trade.cashFlow.receiver) {
      receiverUserId = trade.cashFlow.receiver;
    } else {
      // Fallback for initial offers without cashFlow
      if (trade.type === 'ask') {
        receiverUserId = trade.fromUserId; // fromUser receives payout
      } else if (trade.type === 'offer') {
        receiverUserId = trade.toUserId; // toUser receives payout
      }
    }

    




  }, [trade?.status, trade?.paymentStatus, trade?.shippingStatus, trade?.transferStatus, currentUser?.uid]);

  // Utility to check if address is complete
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  // Refresh tracking status from Shippo
  const refreshTrackingStatus = async ({ silent = false } = {}) => {
    if (!currentUser) {
      console.log('⏳ Skipping tracking refresh - user not authenticated');
      return;
    }
    try {
      setRefreshing(true);
      console.log('🔄 Refreshing tracking status from Shippo...');
      
      const refreshFunction = httpsCallable(functions, 'refreshTrackingStatus');
      const result = await refreshFunction({ tradeId: id });
      
      console.log('✅ Tracking refresh result:', result.data);
      
      // Refresh the trade data to show updated tracking
      await fetchTrade();
      
      if (!silent) {
        alert('Tracking status updated successfully!');
      }
    } catch (error) {
      console.error('❌ Error refreshing tracking:', error);
      if (!silent) {
        alert(`Failed to refresh tracking: ${error.message}`);
      }
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch trade data function - memoized to prevent infinite loops
  const fetchTrade = useCallback(async () => {
    // Wait for user to be authenticated before fetching trade. Kept OUTSIDE the
    // try/finally so we DON'T flip `loading` off (which would flash a terminal
    // state) before auth has resolved on a fresh load / pull-to-refresh reload.
    if (!currentUser) {
      console.log('⏳ Waiting for user authentication before fetching trade...');
      return;
    }

    try {
      console.log('🔍 Fetching trade data for ID:', id, 'User:', currentUser.uid);
      const docRef = doc(db, 'tradeOffers', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const tradeData = { id: docSnap.id, ...docSnap.data() };
        console.log('✅ Trade data fetched successfully:', tradeData.id);
        setTrade(tradeData);
        setNotFound(false);

        // Fetch pedal info for both sides (supports single and multi-pedal trades)
        const allInitiatorPedalIds = getInitiatorPedalIds(tradeData);
        if (allInitiatorPedalIds.length > 0 && tradeData.responderPedalId) {
          let allInitiatorPedals = [];
          let responderPedal = null;
          
          if (tradeData.pedalSnapshots?.initiatorPedal && tradeData.pedalSnapshots?.responderPedal) {
            console.log('📸 Using pedal snapshots from trade acceptance for consistent display');
            // Snapshots may contain single or array of initiator pedals
            if (tradeData.pedalSnapshots.initiatorPedals && Array.isArray(tradeData.pedalSnapshots.initiatorPedals)) {
              allInitiatorPedals = tradeData.pedalSnapshots.initiatorPedals;
            } else {
              allInitiatorPedals = [tradeData.pedalSnapshots.initiatorPedal];
            }
            responderPedal = tradeData.pedalSnapshots.responderPedal;
          } else {
            console.log('🔄 Fetching live pedal data (no snapshots available)');
            const initiatorPromises = allInitiatorPedalIds.map(pid => getDoc(doc(db, 'listings', pid)));
            const responderPedalSnap = await getDoc(doc(db, 'listings', tradeData.responderPedalId));
            const initiatorSnaps = await Promise.all(initiatorPromises);
            allInitiatorPedals = initiatorSnaps
              .filter(s => s.exists())
              .map(s => ({ id: s.id, ...s.data() }));
            responderPedal = responderPedalSnap.exists() ? { id: responderPedalSnap.id, ...responderPedalSnap.data() } : null;
          }
          
          setInitiatorPedals(allInitiatorPedals);
          // myPedal = initiator (fromUser), theirPedal = responder (toUser) — fixed roles, not relative to current user
          setMyPedal(allInitiatorPedals[0] || null);
          setTheirPedal(responderPedal);
        }

        // Fetch usernames from publicProfiles (PII isolation phase 1).
        // Prefer the snapshot baked onto the trade doc, fall back to publicProfiles.
        const fromSnapshotUsername = tradeData.fromUserSnapshot?.username;
        const toSnapshotUsername = tradeData.toUserSnapshot?.username;

        let fromUsername = fromSnapshotUsername || 'User A';
        let toUsername = toSnapshotUsername || 'User B';

        if (!fromSnapshotUsername || !toSnapshotUsername) {
          const [fromSnap, toSnap] = await Promise.all([
            fromSnapshotUsername ? null : getDoc(doc(db, 'publicProfiles', tradeData.fromUserId)),
            toSnapshotUsername ? null : getDoc(doc(db, 'publicProfiles', tradeData.toUserId))
          ]);
          if (fromSnap && fromSnap.exists()) {
            fromUsername = fromSnap.data().username || 'User A';
          }
          if (toSnap && toSnap.exists()) {
            toUsername = toSnap.data().username || 'User B';
          }
        }

        setUsernames({ userA: fromUsername, userB: toUsername });
      } else {
        // Confirmed: the trade document does not exist.
        setNotFound(true);
      }
    } catch (err) {
      console.error('Error loading trade progress:', err);
      // A failed load (no access / network) surfaces the friendly terminal state
      // rather than spinning on "Loading" forever.
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, currentUser]);

  // Listen for auth state changes to handle Stripe redirects
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔐 Auth state changed in TradeProgress:', !!user, user?.uid);
      setCurrentUser(user);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchTrade();
  }, [fetchTrade]); // Re-fetch when user authentication changes

  // Automatically refresh tracking when page loads (if tracking numbers exist and not both delivered)
  useEffect(() => {
    if (!currentUser) return;
    const hasTrackingA = trade?.shippingStatus?.fromUser?.trackingNumber;
    const hasTrackingB = trade?.shippingStatus?.toUser?.trackingNumber;
    const deliveredA = trade?.shippingStatus?.fromUser?.delivered;
    const deliveredB = trade?.shippingStatus?.toUser?.delivered;
    const bothDelivered = deliveredA && deliveredB;
    
    if ((hasTrackingA || hasTrackingB) && !bothDelivered && !refreshing) {
      console.log('🔄 Auto-refreshing trade tracking on page load...');
      refreshTrackingStatus({ silent: true });
    }
  }, [currentUser, trade?.shippingStatus?.fromUser?.trackingNumber, trade?.shippingStatus?.toUser?.trackingNumber, trade?.shippingStatus?.fromUser?.delivered, trade?.shippingStatus?.toUser?.delivered]);

  // Refetch data when returning from payment success
  useEffect(() => {
    const payment = searchParams.get('payment');
    const type = searchParams.get('type');
    
    if (payment === 'success' && (type === 'shipping' || type === 'cash_credit')) {
      // Refetch trade data to show updated payment/label info
      const refetchTrade = async () => {
        try {
          console.log('🔄 Refetching trade data after payment success...');
          const docRef = doc(db, 'tradeOffers', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const tradeData = { id: docSnap.id, ...docSnap.data() };
            console.log('📊 Updated trade data:', JSON.stringify(tradeData, null, 2));
            console.log('💰 Payment status:', JSON.stringify(tradeData.paymentStatus, null, 2));
            setTrade(tradeData);
            
            // Show success message for cash credit payments
            if (type === 'cash_credit') {
              console.log('✅ Showing cash credit success message');
              setPaymentSuccessType('cash_credit');
              setShowPaymentSuccess(true);
              // Hide success message after 5 seconds
              setTimeout(() => setShowPaymentSuccess(false), 5000);
            }
          } else {
            console.error('❌ Trade document not found during refetch');
          }
        } catch (err) {
          console.error('Error refetching trade after payment:', err);
        }
      };
      
      // Small delay to allow database update to complete
      setTimeout(refetchTrade, 2000);
    }
  }, [searchParams, id]);

  // Handle redirect from successful payment
  useEffect(() => {
    const payment = searchParams.get('payment');
    const type = searchParams.get('type');
    
    if (payment === 'success' && type === 'shipping') {
      setPaymentSuccessType('shipping');
      setShowPaymentSuccess(true);
      
      // Clear URL parameters after a short delay to avoid showing success message on refresh
      setTimeout(() => {
        window.history.replaceState({}, '', window.location.pathname);
      }, 100);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowPaymentSuccess(false);
      }, 5000);
    }
  }, [searchParams]);

  // Handle auto-collapse of completed steps 
  useEffect(() => {
    if (!trade || hasInitializedCollapse) return;
    
    // Only calculate if we have user data
    if (!currentUser) return;
    
    const isFromUser = currentUser?.uid === trade.fromUserId;
    const transferSuccessful = trade.transferStatus?.status === 'success';
    
    let receiverUserId = null;
    
    // Prefer cashFlow fields (set during counter offers) for accuracy
    if (trade.cashFlow && trade.cashFlow.receiver) {
      receiverUserId = trade.cashFlow.receiver;
    } else {
      // Fallback for initial offers without cashFlow
      if (trade.type === 'ask') {
        receiverUserId = trade.fromUserId;
      } else if (trade.type === 'offer') {
        receiverUserId = trade.toUserId;
      }
    }

    const userA = {
      id: trade.fromUserId,
      paid: trade.paymentStatus?.fromUserPaid || false,
      payoutReceived: transferSuccessful, // Both users benefit when transfer completes
      photoUploaded: trade.shippingStatus?.fromUserPhotos?.length > 0,
      ready: trade.shippingStatus?.fromUserReady,
      label: trade.shippingStatus?.fromUser?.labelPurchased,
      inTransit: trade.shippingStatus?.fromUser?.inTransit || false,
      delivered: trade.shippingStatus?.fromUser?.delivered || false,
    };

    const userB = {
      id: trade.toUserId,
      paid: trade.paymentStatus?.toUserPaid || false,
      payoutReceived: transferSuccessful, // Both users benefit when transfer completes
      photoUploaded: trade.shippingStatus?.toUserPhotos?.length > 0,
      ready: trade.shippingStatus?.toUserReady,
      label: trade.shippingStatus?.toUser?.labelPurchased,
      inTransit: trade.shippingStatus?.toUser?.inTransit || false,
      delivered: trade.shippingStatus?.toUser?.delivered || false,
    };
    
    // Define steps for collapsing check
    const steps = [
      { label: 'Completed Payment', check: (u) => u.paid || u.payoutReceived },
      { label: 'Upload Confirmation Photo', check: (u) => u.photoUploaded },
      { label: 'Ready to Ship', check: (u) => u.ready },
      { label: 'Generate Shipping Label', check: (u) => u.inTransit || u.delivered },
      { label: 'Item In Transit', check: (u) => u.delivered },
      { label: 'Feedback Provided', check: () => {
        const feedbackField = isFromUser ? 'fromUserFeedback' : 'toUserFeedback';
        return !!trade[feedbackField];
      }}
    ];
    
    const initialCollapsedState = {};
    steps.forEach((step, idx) => {
      const aChecked = step.check(userA);
      const bChecked = step.check(userB);
      // Collapse step if both users have completed it
      if (aChecked && bChecked) {
        initialCollapsedState[idx] = true;
      }
    });
    setCollapsedSteps(initialCollapsedState);
    setHasInitializedCollapse(true);
  }, [trade, hasInitializedCollapse, currentUser]);

  // Auto-expand feedback section when trade becomes complete
  useEffect(() => {
    if (trade?.status === 'complete' && hasInitializedCollapse) {
      // Find the feedback step index (should be step 6, but let's be safe)
      const feedbackStepIndex = 6; // Based on the steps array: Payment(0), Photo(1), Ready(2), Label(3), Transit(4), Delivered(5), Feedback(6)
      
      // Uncollapse the feedback step
      setCollapsedSteps(prev => ({
        ...prev,
        [feedbackStepIndex]: false
      }));
    }
  }, [trade?.status, hasInitializedCollapse]);

  // Scroll to top once per trade, on arrival and again when it first renders.
  // This used to depend on `trade` itself, so every refetch (tracking refresh,
  // Ready to Ship, Mark as Received, label-purchase polling) yanked the user back
  // to the top mid-page. Keyed on `id` only; the ref makes the post-load scroll
  // happen once.
  const scrolledForTradeRef = useRef(null);
  useLayoutEffect(() => {
    if (searchParams.get('scroll') === 'feedback') return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const tradeLoaded = !loading && !!trade;
  useEffect(() => {
    if (!tradeLoaded || scrolledForTradeRef.current === id) return;
    scrolledForTradeRef.current = id;
    if (searchParams.get('scroll') === 'feedback') return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeLoaded, id]);

  // Scroll to feedback section when arriving from the trade complete modal
  useEffect(() => {
    if (searchParams.get('scroll') !== 'feedback') return;
    if (!trade || trade.status !== 'complete') return;
    const timer = setTimeout(() => {
      const el = document.getElementById('tp-feedback-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Strip the param from the URL so bookmarking doesn't re-trigger the scroll
      window.history.replaceState(null, '', window.location.pathname);
    }, 400);
    return () => clearTimeout(timer);
  }, [trade?.status, searchParams]);

  // Auto-expand payment section when 24-hour escrow timer starts
  useEffect(() => {
    if (trade?.transferStatus?.bothPedalsDeliveredAt && hasInitializedCollapse) {
      const paymentStepIndex = 0; // Payment is the first step
      
      // Uncollapse the payment step so users can see the countdown
      setCollapsedSteps(prev => ({
        ...prev,
        [paymentStepIndex]: false
      }));
    }
  }, [trade?.transferStatus?.bothPedalsDeliveredAt, hasInitializedCollapse]);

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    // 🔒 SECURITY: Check if user has completed payment first
    const isFromUser = currentUser.uid === trade.fromUserId;
    const userPaid = isFromUser ? trade.paymentStatus?.fromUserPaid : trade.paymentStatus?.toUserPaid;
    
    if (!userPaid) {
      alert('⚠️ You must complete your payment before uploading a confirmation photo.');
      return;
    }

    // ✅ FILE TYPE VALIDATION: Restrict to specific image formats
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/tiff', 'image/svg+xml'];
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.tiff', '.svg'];
    
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Please upload a valid image file (.jpg, .jpeg, .png, .tiff, .svg)');
      return;
    }

    // ✅ FILE SIZE VALIDATION: 10MB max
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert('File size must be less than 10MB. Please choose a smaller image.');
      return;
    }

    setUploading(true);

    try {
      // 🗜️ COMPRESSION: Compress before upload if > 1.5MB
      const originalSizeMB = file.size / 1024 / 1024;
      let fileToUpload = file;
      
      if (originalSizeMB > 1.5) {
        try {
          const compressionOptions = {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1800,
            useWebWorker: false,
            quality: 0.85,
            fileType: 'image/jpeg'
          };
          
          console.log(`🔄 Compressing confirmation photo (${originalSizeMB.toFixed(2)}MB)...`);
          fileToUpload = await imageCompression(file, compressionOptions);
          const compressedSizeMB = fileToUpload.size / 1024 / 1024;
          const savings = ((1 - fileToUpload.size / file.size) * 100).toFixed(1);
          console.log(`✅ Confirmation photo compressed: ${compressedSizeMB.toFixed(2)}MB (${savings}% smaller)`);
        } catch (compressionError) {
          console.warn('⚠️ Confirmation photo compression failed, uploading original:', compressionError);
          // Continue with original if compression fails — never block the upload
        }
      }

      const storage = getStorage();
      const storageRef = ref(storage, `ship_ready_photos/${id}_${currentUser.uid}`);
      await uploadBytes(storageRef, fileToUpload);
      const url = await getDownloadURL(storageRef);

      const userField = currentUser.uid === trade.fromUserId ? 'fromUserPhotos' : 'toUserPhotos';
      await updateDoc(doc(db, 'tradeOffers', id), {
        [`shippingStatus.${userField}`]: [url],
      });
      
      // Update step timestamp when user uploads photo
      await updateStepTimestamp('photo');
      
      // Refresh trade data to show uploaded photo immediately
      const updatedDocRef = doc(db, 'tradeOffers', id);
      const updatedDocSnap = await getDoc(updatedDocRef);
      if (updatedDocSnap.exists()) {
        setTrade({ id: updatedDocSnap.id, ...updatedDocSnap.data() });
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload photo. Please try again.');
    }

    setUploading(false);
  };

  const handleReportPhoto = async (photoUrl, reportedUserId, reportedUsername) => {
    const reportingUsername = usernames[currentUser.uid === trade.fromUserId ? 'userA' : 'userB'];
    
    // Open contact modal
    openContact({
      category: 'report_photo',
      subject: `Report Photo in Trade ${id}`,
      message: `Please describe the issue with the photo:\n\n`,
      context: {
        tradeId: id,
        photoUrl: photoUrl,
        reportedUser: reportedUsername,
        reportedUserId: reportedUserId,
        reportingUser: reportingUsername,
        timestamp: new Date().toLocaleString()
      }
    });

    try {
      // Log the report to Firestore for admin review
      const reportData = {
        tradeId: id,
        photoUrl: photoUrl,
        reportedUserId: reportedUserId,
        reportedUsername: reportedUsername,
        reportingUserId: currentUser.uid,
        reportingUsername: reportingUsername,
        reason: 'Reported via contact form',
        timestamp: new Date(),
        status: 'pending' // pending, reviewed, resolved
      };

      // Add to a reports collection for admin visibility
      await addDoc(collection(db, 'photoReports'), reportData);
      console.log('📝 Photo reported:', reportData);
    } catch (err) {
      console.error('Failed to log photo report:', err);
    }
  };

  const handleReportIssue = async () => {
    const reportingUsername = usernames[currentUser.uid === trade.fromUserId ? 'userA' : 'userB'];
    const otherUsername = usernames[currentUser.uid === trade.fromUserId ? 'userB' : 'userA'];

    openContact({
      category: 'report_trade',
      subject: `Trade Issue Report - Trade ${id}`,
      message: `Please describe the issue with this trade:\n\n`,
      context: {
        tradeId: id,
        reportingUser: reportingUsername,
        otherUser: otherUsername,
        timestamp: new Date().toLocaleString()
      }
    }, async () => {
      try {
        const tradeRef = doc(db, 'tradeOffers', id);
        await updateDoc(tradeRef, {
          disputeHold: true,
          disputeHoldAt: serverTimestamp(),
          disputeHoldBy: currentUser?.uid,
          disputeHoldReason: 'User reported issue'
        });

        await addDoc(collection(db, 'tradeIssues'), {
          tradeId: id,
          issueType: 'Reported via contact form',
          description: 'User reported issue via contact form',
          reportingUserId: currentUser.uid,
          reportingUsername: reportingUsername,
          otherUserId: currentUser.uid === trade.fromUserId ? trade.toUserId : trade.fromUserId,
          otherUsername: otherUsername,
          timestamp: new Date(),
          status: 'pending',
          disputeHoldPlaced: true
        });

        console.log('Dispute hold placed and trade issue reported');
      } catch (err) {
        console.error('Failed to place dispute hold:', err);
      }
    });
  };

  // Helper function to update step completion timestamp
  const updateStepTimestamp = async (stepId) => {
    if (!currentUser?.uid) return;
    
    try {
      const updateStepTimestampFn = httpsCallable(functions, 'updateStepTimestamp');
      await updateStepTimestampFn({
        tradeId: id,
        stepId: stepId,
        userId: currentUser.uid
      });
      console.log(`✅ Updated timestamp for step: ${stepId}`);
    } catch (error) {
      console.error(`❌ Error updating timestamp for step ${stepId}:`, error);
    }
  };

  const handleRefreshStatus = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    try {
      console.log('🔄 Refreshing trade status...');
      const refreshTradeStatus = httpsCallable(functions, 'refreshTradeStatus');
      const result = await refreshTradeStatus({ tradeId: id });
      
      console.log('✅ Refresh result:', result.data);
      
      if (result.data.statusChanged) {
        // Refetch the trade data to show the updated status
        const tradeRef = doc(db, 'tradeOffers', id);
        const tradeSnap = await getDoc(tradeRef);
        if (tradeSnap.exists()) {
          const updatedTrade = { id: tradeSnap.id, ...tradeSnap.data() };
          setTrade(updatedTrade);
          console.log('📊 Trade data refreshed');
        }
      }
    } catch (error) {
      console.error('❌ Error refreshing status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleReadyToShip = async (userId, isChecked) => {
    // 🔒 SECURITY: Check if user has completed payment before marking ready
    if (isChecked) {
      const isFromUser = userId === trade.fromUserId;
      const userPaid = isFromUser ? trade.paymentStatus?.fromUserPaid : trade.paymentStatus?.toUserPaid;
      
      if (!userPaid) {
        alert('⚠️ You must complete your payment before marking yourself as ready to ship.');
        return;
      }
      
      // Also check if user has uploaded a confirmation photo
      const userPhotos = isFromUser ? trade.shippingStatus?.fromUserPhotos : trade.shippingStatus?.toUserPhotos;
      if (!userPhotos || userPhotos.length === 0) {
        alert('⚠️ You must upload a confirmation photo before marking yourself as ready to ship.');
        return;
      }
    }
    
    const field = userId === trade.fromUserId
      ? 'fromUserReady'
      : 'toUserReady';

    const newStatus = {
      ...trade.shippingStatus,
      [field]: isChecked,
    };

    const bothReady = newStatus.fromUserReady && newStatus.toUserReady;
    const neitherReady = !newStatus.fromUserReady && !newStatus.toUserReady;

    const update = {
      shippingStatus: newStatus,
    };

    // If both are ready and we haven't sent email yet, trigger it
    if (bothReady && !trade.triggeredReadyEmail) {
      update.triggerReadyToShipEmail = true;
    }

    // If neither user is ready, reset the email flags to allow re-triggering.
    // NOTE: deliberately does NOT gate on the local `trade.triggeredReadyEmail`.
    // That flag is set server-side by notifyReadyToShip after it fires, so the
    // client's copy is often stale; gating on it left the guard stuck `true`
    // and silently blocked any re-fire. Clearing whenever neither user is ready
    // is always correct (a sent/pending ready-email state is meaningless when
    // nobody is ready) and is a harmless no-op when the flags are already clear.
    if (neitherReady) {
      update.triggeredReadyEmail = false;
      update.triggerReadyToShipEmail = false;
    }

    await updateDoc(doc(db, 'tradeOffers', id), update);
    setTrade({ ...trade, ...update });
    
    // Update step timestamp when user marks themselves as ready
    if (isChecked && userId === currentUser?.uid) {
      await updateStepTimestamp('ready');
    }
  };

  const openConfirmationPhoto = (photos, index, userName) => {
    setPhotoOverlay({
      isOpen: true,
      url: photos[index],
      alt: `${userName}'s confirmation photo`,
      photos,
      index,
    });
  };

  const closeConfirmationPhoto = () => {
    setPhotoOverlay({ isOpen: false, url: '', alt: '', photos: [], index: 0 });
  };

  const showNextConfirmationPhoto = () => {
    setPhotoOverlay((prev) => {
      if (prev.index >= prev.photos.length - 1) return prev;
      const nextIndex = prev.index + 1;
      return { ...prev, index: nextIndex, url: prev.photos[nextIndex] };
    });
  };

  const showPrevConfirmationPhoto = () => {
    setPhotoOverlay((prev) => {
      if (prev.index <= 0) return prev;
      const prevIndex = prev.index - 1;
      return { ...prev, index: prevIndex, url: prev.photos[prevIndex] };
    });
  };

  const handleManualDeliveryConfirmation = async (userField) => {
    try {
      const field = userField === 'fromUser' ? 'fromUser' : 'toUser';
      const isFromUser = currentUser?.uid === trade.fromUserId;
      const isReceivingPackage = (field === 'fromUser' && !isFromUser) || (field === 'toUser' && isFromUser);

      if (!isReceivingPackage) {
        console.error(`❌ Delivery confirmation rejected: user ${currentUser?.uid} is not the receiver of ${userField}'s package`);
        return;
      }
      
      const confirmed = window.confirm(
        `Are you sure you want to mark this package as RECEIVED?\n\nOnly confirm this if you have physically received the pedal.`
      );
      
      if (!confirmed) {
        return; // User cancelled
      }

      setMarkingReceived(userField);
      
      console.log(`📦 Manual delivery confirmation for ${userField}`);
      
      // Update the trade document
      await updateDoc(doc(db, 'tradeOffers', id), {
        [`shippingStatus.${field}.delivered`]: true,
        [`shippingStatus.${field}.deliveredAt`]: serverTimestamp()
      });
      
      // Update step timestamp when marking as delivered
      await updateStepTimestamp('delivered');
      
      console.log('✅ Marked as delivered, waiting for backend to process...');
      
      // Wait a moment for the backend trigger to process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Refetch the trade data to get the updated state
      await fetchTrade();
      
      // Wait another moment and refetch again to ensure we catch any backend updates
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchTrade();
      
      console.log('✅ Trade data refreshed after delivery confirmation');
    } catch (error) {
      console.error('Error marking as delivered:', error);
      alert('Failed to mark as delivered. Please try again.');
    } finally {
      setMarkingReceived(null);
    }
  };

  const handleGetShippingRates = async () => {
    setLoadingRates(true);
    
    const isFromUser = currentUser?.uid === trade?.fromUserId;
    const initiatorTotalPrice = initiatorPedals.length > 0
      ? initiatorPedals.reduce((sum, p) => sum + (Number(p?.price) || 0), 0)
      : (Number(myPedal?.price) || 100);
    const pedalInsuranceValue = isFromUser ? initiatorTotalPrice : (Number(theirPedal?.price) || 100);
    
    const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-pedalxchange.cloudfunctions.net';
    const cacheBuster = Date.now();
    const fullUrl = `${functionsUrl}/getShippoRates?t=${cacheBuster}`;
    console.log('🔍 Functions URL:', functionsUrl);
    console.log('🔍 Full URL:', fullUrl);
    
    try {
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store',
        body: JSON.stringify({
          tradeId: id,
          fromAddress: packageData.fromAddress,
          toAddress: packageData.toAddress,
          parcel: {
            length: parseFloat(packageData.length),
            width: parseFloat(packageData.width),
            height: parseFloat(packageData.height),
            weight: parseFloat(packageData.weight),
            distance_unit: 'in',
            mass_unit: 'lb',
            value: pedalInsuranceValue
          },
          userId: currentUser.uid,
          timestamp: Date.now() // Add timestamp to prevent caching
        })
      });

      console.log('📊 Response status:', response.status);
      console.log('📊 Response headers:', response.headers);
      
      const result = await response.json();
      
      if (result.success) {
        setShippingRates(result.rates);
        console.log('✅ Retrieved shipping rates:', result.rates);
      } else {
        console.error('❌ Error getting shipping rates:', result.error);
        alert('Error getting shipping rates: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error getting shipping rates:', error);
      alert('Error getting shipping rates: ' + error.message + 
            '\n\nPlease check:\n' +
            '• Package dimensions are filled out\n' +
            '• All address fields are complete\n' +
            '• You have internet connectivity\n' +
            '• Try again in a few seconds');
    }
    setLoadingRates(false);
  };

  const handleCreateCheckout = async (rate) => {
    setCreatingCheckout(true);
    console.log('🚀 Starting checkout creation for rate:', rate);
    console.log('🚀 Trade ID:', id);
    console.log('🚀 User ID:', currentUser.uid);
    
    try {
      const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-pedalxchange.cloudfunctions.net';
      const fullUrl = `${functionsUrl}/createLabelCheckoutSession`;
      
      console.log('🔍 Functions URL:', functionsUrl);
      console.log('🔍 Full URL:', fullUrl);
      
      const isFromUser = currentUser?.uid === trade?.fromUserId;
      const initiatorTotalPrice = initiatorPedals.length > 0
        ? initiatorPedals.reduce((sum, p) => sum + (Number(p?.price) || 0), 0)
        : (Number(myPedal?.price) || 100);
      const pedalInsuranceValue = isFromUser ? initiatorTotalPrice : (Number(theirPedal?.price) || 100);

      const requestBody = {
        tradeId: id,
        rateObjectId: rate.rateObjectId,
        totalAmount: rate.amount,
        rateDetails: {
          provider: rate.provider,
          servicelevel: rate.servicelevel,
          originalAmount: rate.shippoRate || (rate.amount - 2),
          markupAmount: rate.platformFee || 2.00
        },
        userId: currentUser.uid,
        insuranceAmount: pedalInsuranceValue,
        returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
      };
      
      console.log('📤 Request body:', requestBody);
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📊 Response status:', response.status);
      console.log('📊 Response ok:', response.ok);
      
      const result = await response.json();
      console.log('📊 Response result:', result);
      
      if (result.success) {
        console.log('✅ Checkout created successfully, redirecting to:', result.checkoutUrl);
        // Native: in-app browser sheet. `onReturn` ONLY fires on native — on web
        // openCheckout does a full-page redirect and Stripe's success_url brings
        // the user back to ?payment=success&type=shipping, handled by the refetch
        // effect above. This page has NO live listener (fetchTrade is a one-time
        // getDoc), so on native we must refetch ourselves when the sheet closes.
        // The label is written by the Stripe webhook -> Shippo purchase, which
        // takes a few seconds, so poll until it lands (or attempts run out; the
        // user can also pull to refresh).
        await openCheckout(result.checkoutUrl, {
          onReturn: () => {
            let attempts = 0;
            const maxAttempts = 6;
            const isFromUser = currentUser?.uid === trade?.fromUserId;
            const poll = async () => {
              attempts += 1;
              try {
                await fetchTrade();
                const snap = await getDoc(doc(db, 'tradeOffers', id));
                const t = snap.exists() ? snap.data() : null;
                const mineLabel = isFromUser
                  ? t?.shippingStatus?.fromUser?.labelPurchased
                  : t?.shippingStatus?.toUser?.labelPurchased;
                if (mineLabel) {
                  // Label confirmed — close the shipping form + rate picker. These
                  // are LOCAL state, so the refetch alone doesn't clear them; on web
                  // the full-page redirect remounts the component (fresh state), but
                  // on native the page stays mounted and the stale "Select Shipping
                  // Option" list would sit under the purchased-label card.
                  handleCloseShippingForm();
                } else if (attempts < maxAttempts) {
                  setTimeout(poll, 2000);
                }
              } catch (err) {
                console.error('❌ Error refetching trade after label purchase:', err);
                if (attempts < maxAttempts) setTimeout(poll, 2000);
              }
            };
            setTimeout(poll, 1500);
          }
        });
      } else {
        console.error('❌ Checkout creation failed:', result.error);
        alert('Error creating checkout: ' + result.error);
      }
    } catch (error) {
      console.error('❌ Error creating checkout:', error);
      console.error('❌ Error stack:', error.stack);
      alert('Payment setup failed: Internal error\n\nDetails: ' + error.message);
    }
    setCreatingCheckout(false);
  };

  const handleOpenShippingForm = async () => {
    // Auto-fill fromAddress with user's saved shippingAddress if not already filled
    if (isAddressComplete(shippingAddress)) {
      // Get current user's email, phone, and full name from Firestore
      const currentUserEmail = auth.currentUser?.email || '';
      
      // Get current user's phone and full name from Firestore
      let currentUserPhone = '';
      let currentUserFullName = shippingAddress.name || ''; // Fallback to shippingAddress.name
      try {
        const currentUserDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (currentUserDoc.exists()) {
          const currentUserData = currentUserDoc.data();
          currentUserPhone = currentUserData.phoneNumber || '';
          
          // 🔧 FIX: Use firstName + lastName if available, otherwise use shippingAddress.name
          if (currentUserData.firstName && currentUserData.lastName) {
            currentUserFullName = `${currentUserData.firstName} ${currentUserData.lastName}`;
          }
        }
      } catch (err) {
        console.error('Failed to fetch current user phone:', err);
      }
      
      setPackageData(prev => ({
        ...prev,
        fromAddress: {
          ...prev.fromAddress,
          ...shippingAddress,
          name: currentUserFullName, // 🔧 FIX: Use full name instead of shippingAddress.name
          country: shippingAddress.country || 'US',
          email: currentUserEmail,
          phone: formatPhoneNumber(currentUserPhone || '')
        }
      }));
      
      console.log('🏠 Auto-filled fromAddress with full name, email and phone:', { name: currentUserFullName, email: currentUserEmail, phone: currentUserPhone });
    }
    
    // Auto-fill toAddress with the other user's saved shippingAddress
    let otherUserId = null;
    if (trade) {
      const currentUserId = auth.currentUser?.uid;
      otherUserId = currentUserId === trade.fromUserId ? trade.toUserId : trade.fromUserId;
    }
    if (otherUserId) {
      try {
        // Read counterparty PII from the snapshot baked onto the trade doc at
        // acceptance (PII isolation phase 1, group B). Falls back to a direct
        // users/{otherUserId} read for legacy trades whose snapshot hasn't
        // been backfilled yet — that fallback will fail once the users read
        // rule is tightened, which is intentional.
        const isCurrentUserFromUser = auth.currentUser?.uid === trade.fromUserId;
        const otherUserSnapshot = isCurrentUserFromUser ? trade.toUserSnapshot : trade.fromUserSnapshot;

        let otherUserData = otherUserSnapshot;
        if (!otherUserData) {
          console.warn('📦 No counterparty snapshot on trade; falling back to users/ read (will fail post-rule-tightening)');
          const otherUserDoc = await getDoc(doc(db, 'users', otherUserId));
          if (otherUserDoc.exists()) {
            otherUserData = otherUserDoc.data();
          }
        }

        if (otherUserData && isAddressComplete(otherUserData.shippingAddress)) {
          const otherUserEmail = otherUserData.email || '';
          const otherUserPhone = otherUserData.phoneNumber || '';

          let otherUserFullName = otherUserData.shippingAddress.name || '';
          if (otherUserData.firstName && otherUserData.lastName) {
            otherUserFullName = `${otherUserData.firstName} ${otherUserData.lastName}`;
          }

          setPackageData(prev => ({
            ...prev,
            toAddress: {
              ...prev.toAddress,
              ...otherUserData.shippingAddress,
              name: otherUserFullName,
              country: otherUserData.shippingAddress.country || 'US',
              email: otherUserEmail,
              phone: formatPhoneNumber(otherUserPhone || '')
            }
          }));

          console.log('📦 Auto-filled toAddress from snapshot:', { name: otherUserFullName, email: otherUserEmail, phone: otherUserPhone });
        }
      } catch (err) {
        console.error('Failed to fetch other user shipping address:', err);
      }
    }
    setShowShippingForm(true);
    setShippingRates([]);
    setSelectedRate(null);
  };

  const handleCloseShippingForm = () => {
    setShowShippingForm(false);
    setShippingRates([]);
    setSelectedRate(null);
  };

  // Auth still resolving (e.g. right after a pull-to-refresh reload) — show a
  // neutral loading state instead of prematurely deciding login/not-found.
  if (!authChecked) return <div className="p-4">Loading trade progress...</div>;

  if (!currentUser) {
    // Auth resolved to logged-out: prompt login (save URL to return after).
    sessionStorage.setItem('returnAfterLogin', window.location.pathname);
    return (
      <div className="p-4 text-red-600">
        Please log in to view this trade.{' '}
        <a href="/login" style={{ color: '#2563eb', textDecoration: 'underline' }}>
          Click here to log in
        </a>
      </div>
    );
  }

  if (loading) return <div className="p-4">Loading trade progress...</div>;

  // Only after a completed fetch with a real user do we surface "not found".
  if (notFound) {
    return (
      <div className="p-4 text-gray-600">
        We couldn&apos;t find this trade. It may have been removed, or you may not have access to it.
      </div>
    );
  }

  // Data still settling (transient null between fetches) — keep showing loading
  // rather than a misleading not-found flash.
  if (!trade) return <div className="p-4">Loading trade progress...</div>;

  const isFromUser = currentUser?.uid === trade.fromUserId;
  const isToUser = currentUser?.uid === trade.toUserId;

  // Determine who receives the payout based on trade type  
  const transferSuccessful = trade.transferStatus?.status === 'success';
  
  let receiverUserId = null;
  
  // Prefer cashFlow fields (set during counter offers) for accuracy
  if (trade.cashFlow && trade.cashFlow.receiver) {
    receiverUserId = trade.cashFlow.receiver;
  } else {
    // Fallback for initial offers without cashFlow
    if (trade.type === 'ask') {
      receiverUserId = trade.fromUserId; // fromUser receives payout
    } else if (trade.type === 'offer') {
      receiverUserId = trade.toUserId; // toUser receives payout
    }
  }



  // Define user objects locally
  const userA = {
    id: trade.fromUserId,
    paid: trade.paymentStatus?.fromUserPaid || false,
    payoutReceived: transferSuccessful, // Both users benefit when transfer completes
    photoUploaded: trade.shippingStatus?.fromUserPhotos?.length > 0,
    ready: trade.shippingStatus?.fromUserReady,
    label: trade.shippingStatus?.fromUser?.labelPurchased,
    labelUrl: trade.shippingStatus?.fromUser?.labelUrl,
    tracking: trade.shippingStatus?.fromUser?.trackingNumber,
    shipReadyConfirmed: trade.shippingStatus?.fromUser?.shipReadyConfirmed,
    delivered: trade.shippingStatus?.fromUser?.delivered || false,
    deliveredAt: trade.shippingStatus?.fromUser?.deliveredAt,
    inTransit: trade.shippingStatus?.fromUser?.inTransit || false,
    currentTrackingStatus: trade.shippingStatus?.fromUser?.currentTrackingStatus,
    initialTrackingStatus: trade.shippingStatus?.fromUser?.initialTrackingStatus,
    lastTrackingUpdate: trade.shippingStatus?.fromUser?.lastTrackingUpdate,
    trackingHistory: trade.shippingStatus?.fromUser?.trackingHistory || [],
  };

  const userB = {
    id: trade.toUserId,
    paid: trade.paymentStatus?.toUserPaid || false,
    payoutReceived: transferSuccessful, // Both users benefit when transfer completes
    photoUploaded: trade.shippingStatus?.toUserPhotos?.length > 0,
    ready: trade.shippingStatus?.toUserReady,
    label: trade.shippingStatus?.toUser?.labelPurchased,
    labelUrl: trade.shippingStatus?.toUser?.labelUrl,
    tracking: trade.shippingStatus?.toUser?.trackingNumber,
    shipReadyConfirmed: trade.shippingStatus?.toUser?.shipReadyConfirmed,
    delivered: trade.shippingStatus?.toUser?.delivered || false,
    deliveredAt: trade.shippingStatus?.toUser?.deliveredAt,
    inTransit: trade.shippingStatus?.toUser?.inTransit || false,
    currentTrackingStatus: trade.shippingStatus?.toUser?.currentTrackingStatus,
    initialTrackingStatus: trade.shippingStatus?.toUser?.initialTrackingStatus,
    lastTrackingUpdate: trade.shippingStatus?.toUser?.lastTrackingUpdate,
    trackingHistory: trade.shippingStatus?.toUser?.trackingHistory || [],
  };

  const steps = [
    {
      label: 'Completed Payment',
      check: (u) => u.paid || u.payoutReceived,
    },
    {
      label: 'Upload Confirmation Photo',
      check: (u) => u.photoUploaded,
    },
    {
      label: 'Ready to Ship',
      check: (u) => u.ready,
      action: true,
    },
    {
      label: 'Generate Shipping Label',
      check: (u) => u.label,
    },
    {
      // Completes on delivery; see the note on the first steps array.
      label: 'Item In Transit',
      check: (u) => u.delivered,
    },
    {
      label: 'Feedback Provided',
      check: () => {
        if (!trade) return false;
        const isFromUser = currentUser?.uid === trade.fromUserId;
        const feedbackField = isFromUser ? 'fromUserFeedback' : 'toUserFeedback';
        return !!trade[feedbackField];
      },
    },
  ];

  const userAName = usernames.userA;
  const userBName = usernames.userB;

  // ---- Timeline redesign helpers (presentation only) ----
  const getInitial = (name) => ((name || '?').trim().charAt(0) || '?').toUpperCase();
  const getAvatarStyle = (side) =>
    side === 'A'
      ? { backgroundColor: '#e0edff', color: '#3b5bdb' }
      : { backgroundColor: '#efe3ff', color: '#7048e8' };

  const stepTitleMap = {
    'Completed Payment': 'Payment and payout',
    'Upload Confirmation Photo': 'Confirmation photos',
    'Ready to Ship': 'Ready to ship',
    'Generate Shipping Label': 'Shipping label',
    'Item In Transit': 'Item in transit',
    'Feedback Provided': 'Feedback',
  };

  const milestoneTotal = steps.length;
  const milestoneComplete = steps.filter((s) => s.check(userA) && s.check(userB)).length;

  const overallStatus = (() => {
    if (trade.status === 'complete' && trade.completedBy === 'admin') {
      return { label: 'Closed by support', tone: 'amber' };
    }
    if (trade.status === 'complete') return { label: 'Complete', tone: 'green' };

    // Reflect the first step both traders haven't finished — not the furthest
    // partial progress (e.g. one photo uploaded while payment is still open).
    for (const step of steps) {
      const aDone = step.check(userA);
      const bDone = step.check(userB);
      if (!aDone || !bDone) {
        const label = stepTitleMap[step.label] || step.label;
        const anyProgress = aDone || bDone;
        const tone = anyProgress ? 'blue' : (step.label === 'Completed Payment' ? 'amber' : 'blue');
        return { label, tone };
      }
    }
    return { label: 'Complete', tone: 'green' };
  })();

  const getStepSubtitle = (step, a, b) => {
    switch (step.label) {
      case 'Completed Payment':
        if (a && b) {
          if (trade.transferStatus) {
            return userA.payoutReceived && userB.payoutReceived
              ? 'Both traders paid, payout complete'
              : 'Both traders paid, payout still pending';
          }
          return 'Both traders paid';
        }
        return a || b ? '1 of 2 payments complete' : 'Waiting for payment';
      case 'Upload Confirmation Photo':
        if (a && b) return 'Both photos uploaded and verified';
        return a || b ? '1 of 2 photos uploaded' : 'Awaiting confirmation photos';
      case 'Ready to Ship':
        if (a && b) return 'Both traders confirmed';
        return a || b ? '1 of 2 confirmed' : 'Waiting for both to confirm';
      case 'Generate Shipping Label':
        if (a && b) return 'Labels purchased for both packages';
        return a || b ? '1 of 2 labels purchased' : 'Purchase shipping labels';
      case 'Item In Transit': {
        if (userA.delivered && userB.delivered) {
          const lu = userA.lastTrackingUpdate || userB.lastTrackingUpdate;
          const luStr = lu ? new Date(lu.seconds * 1000).toLocaleDateString() : null;
          return luStr ? `Delivered, last updated ${luStr}` : 'Delivered';
        }
        if (userA.delivered || userB.delivered) return '1 of 2 delivered';
        if (userA.inTransit || userB.inTransit) return 'Package(s) in transit';
        return 'Awaiting shipment';
      }
      case 'Feedback Provided':
        return a && b ? 'Feedback submitted' : 'Share your trade experience';
      default:
        return '';
    }
  };

  return (
    <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      {!userLoading && !isAddressComplete(shippingAddress) && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 rounded">
          <p className="mb-2 font-semibold">Shipping address required</p>
          <p className="mb-2">You must complete your shipping address before progressing in a trade.</p>
          <button
            type="button"
            onClick={() => setShowAddressModal(true)}
            className="text-blue-700 underline"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
          >
            Add address
          </button>
        </div>
      )}
      <h2 className="text-2xl font-bold mb-4">Trade Progress</h2>

      {/* Trade Details Section */}
      <style>{`
        .tp-pedal-link {
          display: flex;
          align-items: center;
          gap: 6px;
          text-decoration: none;
          color: inherit;
        }
        .tp-pedal-thumb {
          width: 88px;
          height: 88px;
          border-radius: 10px;
          overflow: hidden;
          background-color: #f3f4f6;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tp-pedal-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        @media (min-width: 520px) {
          .tp-pedal-thumb {
            width: 96px;
            height: 96px;
          }
        }
        .tp-trade-center {
          flex: 0 0 auto;
          text-align: center;
          align-self: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 0 8px;
        }
        .tp-swap-badge {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #fafafa;
          border: 1px solid #e5e7eb;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          flex-shrink: 0;
        }
        .tp-swap-badge svg {
          width: 30px;
          height: 30px;
        }
        .tp-trade-type {
          font-size: 12px;
          font-weight: 600;
          color: #059669;
          line-height: 1.3;
          text-align: center;
        }
        .tp-tl-headbox-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: stretch;
          gap: 0.75rem;
          margin-top: 14px;
        }
        .tp-tl-action-btn {
          flex: 1 1 calc(50% - 0.375rem);
          min-width: 132px;
          max-width: 220px;
          padding: 0.75rem 1rem;
          border-radius: 9999px;
          border: 1px solid #d1d5db;
          background-color: #ffffff;
          color: #374151;
          font-size: 0.875rem;
          font-weight: 600;
          font-family: 'Nunito', sans-serif;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          text-decoration: none;
          cursor: pointer;
          box-sizing: border-box;
          line-height: 1.2;
          white-space: nowrap;
        }
        .tp-tl-action-btn:hover:not(:disabled) {
          background-color: #f9fafb;
        }
        .tp-tl-action-btn:disabled {
          background-color: #f3f4f6;
          color: #9ca3af;
          border-color: #e5e7eb;
          cursor: not-allowed;
        }
        .tp-tl-action-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #e57373;
          display: inline-block;
          flex-shrink: 0;
        }
        .tp-tl-action-dot.disabled {
          background-color: #9ca3af;
        }
        @media (max-width: 768px) {
          .tp-pedal-card,
          .tp-report-btn {
            width: calc(100% - 1rem) !important;
            margin-left: 0.5rem !important;
            margin-right: 0.5rem !important;
          }
        }
        .tp-multi-grid {
          display: grid;
          grid-template-columns: repeat(2, 64px);
          gap: 6px;
          justify-content: center;
          margin-bottom: 4px;
        }
        .tp-multi-tile {
          width: 64px;
          height: 64px;
        }
        .tp-multi-tile img {
          width: 64px;
          height: 64px;
        }
        .tp-multi-label { font-size: 9px; }
        .tp-multi-price { font-size: 9px; }
        @media (min-width: 480px) {
          .tp-multi-grid {
            grid-template-columns: repeat(2, 80px);
            gap: 6px;
            margin-bottom: 6px;
          }
          .tp-multi-tile {
            width: 80px;
            height: 80px;
          }
          .tp-multi-tile img {
            width: 80px;
            height: 80px;
          }
          .tp-multi-label { font-size: 11px; }
          .tp-multi-price { font-size: 10px; }
        }
        @media (max-width: 480px) {
          .tp-pedal-link:not(.tp-pedal-link-multi) {
            flex-direction: column !important;
            align-items: center !important;
            gap: 2px !important;
          }
          .tp-pedal-link:not(.tp-pedal-link-multi) .tp-pedal-name-above {
            display: none !important;
          }
          .tp-pedal-link:not(.tp-pedal-link-multi) .tp-pedal-text-beside {
            text-align: center !important;
            margin-top: 4px !important;
          }
          .tp-pedal-link:not(.tp-pedal-link-multi) .tp-pedal-text-beside div:first-child {
            font-size: 11px !important;
          }
          .tp-pedal-link:not(.tp-pedal-link-multi) .tp-pedal-text-beside div:last-child {
            font-size: 10px !important;
          }
          .tp-pedal-link:not(.tp-pedal-link-multi) .tp-pedal-price-below {
            display: none !important;
          }
          .tp-initiator-multi {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 6px !important;
          }
          .tp-head-col {
            align-items: center !important;
          }
          .tp-pedal-link-multi .tp-pedal-thumb {
            width: 72px;
            height: 72px;
          }
          .tp-pedal-link-multi {
            flex-direction: column !important;
            align-items: center !important;
            gap: 2px !important;
            text-align: center;
          }
          .tp-pedal-link-multi .tp-pedal-text-beside {
            text-align: center !important;
          }
          .tp-pedal-link-multi .tp-pedal-text-beside div:first-child {
            font-size: 11px !important;
          }
          .tp-pedal-link-multi .tp-pedal-text-beside div:last-child {
            font-size: 10px !important;
          }
        }
        .tp-toggle {
          position: relative;
          width: 44px;
          height: 26px;
          background-color: #d1d5db;
          border-radius: 9999px;
          cursor: pointer;
          transition: background-color 0.2s;
          border: none;
          padding: 0;
          flex-shrink: 0;
        }
        .tp-toggle.active {
          background-color: #4db6ac;
        }
        .tp-toggle.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .tp-toggle::after {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 22px;
          height: 22px;
          background-color: white;
          border-radius: 50%;
          transition: transform 0.2s;
        }
        .tp-toggle.active::after {
          transform: translateX(18px);
        }
        .tp-toggle-readonly {
          cursor: default;
          pointer-events: none;
          background-color: #e5e7eb;
        }
        .tp-toggle-readonly.active {
          background-color: #c4d4d2;
        }
        .tp-toggle-readonly::after {
          background-color: #f3f4f6;
          box-shadow: none;
        }
        .tp-toggle-readonly.active::after {
          background-color: #eef2f1;
        }
        .tp-toggle-readonly-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #9ca3af;
          line-height: 1.25;
          min-width: 0;
          flex: 1;
        }
        .tp-toggle-label {
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.25;
          min-width: 0;
          flex: 1;
        }
        .tp-toggle-row {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
          max-width: 100%;
        }
        @media (max-width: 768px) {
          .tp-toggle-readonly-label,
          .tp-toggle-label {
            font-size: 0.75rem;
          }
          .tp-toggle-row {
            margin-top: 20px;
          }
        }
      `}</style>
      <div style={{ maxWidth: '768px', width: '100%', margin: '0 auto', padding: '0 0.5rem', boxSizing: 'border-box' }}>
        <div className="tp-tl-headbox">
        {myPedal && theirPedal && (
        <>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          gap: '0',
        }}>
          {/* FromUser's Pedal(s) (Left Column) */}
          <div className="tp-head-col" style={{ flex: '1 1 35%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(() => {
              const pedalsToShow = initiatorPedals.length > 0 ? initiatorPedals : (myPedal ? [myPedal] : []);
              const isMulti = pedalsToShow.length > 1;
              const isViewerInitiator = currentUser?.uid === trade.fromUserId;
              const leftSide = isViewerInitiator ? 'my' : 'their';
              return (
                <>
                  <div className={isMulti ? 'tp-initiator-multi' : ''} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {pedalsToShow.map((pedal, idx) => (
                      <Link
                        key={pedal?.id || idx}
                        to={`/archived-listing/trade/${trade.id}/${leftSide}${pedal?.id ? `/${pedal.id}` : ''}`}
                        className={isMulti ? 'tp-pedal-link tp-pedal-link-multi' : 'tp-pedal-link'}
                      >
                        <div className="tp-pedal-name-above" style={{ fontWeight: 600, fontSize: '12.5px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3', textAlign: 'left', display: 'none' }}>{pedal?.make} {pedal?.model}</div>
                        <div className="tp-pedal-thumb">
                          {(pedal?.images?.top || placeholderFor(pedal || {}).url) ? (
                            <img src={pedal?.images?.top || placeholderFor(pedal || {}).url} alt={`${pedal?.make} ${pedal?.model}`} />
                          ) : (
                            <span style={{ fontSize: '10px', color: '#9ca3af' }}>No Image</span>
                          )}
                        </div>
                        <div className="tp-pedal-text-beside" style={{ textAlign: 'left', minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3' }}>{pedal?.make} {pedal?.model}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>${pedal?.price || 0}</div>
                        </div>
                        <div className="tp-pedal-price-below" style={{ fontSize: '12px', color: '#6b7280', display: 'none' }}>${pedal?.price || 0}</div>
                      </Link>
                    ))}
                  </div>
                </>
              );
            })()}
            {trade && auth.currentUser && auth.currentUser.uid === trade.fromUserId && trade.protection && trade.protection.fromUser && trade.paymentStatus?.fromUserPaid && (
              /* Shown only once this user's payment is COMPLETE (Jason,
                 2026-09-18): under the default-ON toggle the protection flag
                 alone means merely "selected", which predates any payment and
                 is irrelevant here. Inline styles on purpose — the previous
                 Tailwind centering classes are inert in this project, so the
                 badge sat left-aligned. */
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#1e40af', textAlign: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '12px' }}>Trade Protection Purchased</span>
              </div>
            )}
          </div>
          {/* Center Section with Trade Arrow and Cash */}
          <div className="tp-trade-center">
            <div className="tp-swap-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M9 8L5 12l4 4" />
                <path d="M15 16l4-4-4-4" />
              </svg>
            </div>
            
            {trade && Number(trade.cashAmount) > 0 ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '12px', fontWeight: 600, color: '#059669',
                backgroundColor: '#ecfdf5', padding: '4px 10px', borderRadius: '9999px', whiteSpace: 'nowrap'
              }}>
                {(() => {
                  let cashGoesToLeft = false;
                  if (trade.cashFlow && trade.cashFlow.receiver) {
                    cashGoesToLeft = trade.cashFlow.receiver === trade.fromUserId;
                  } else {
                    cashGoesToLeft = trade.type === 'ask';
                  }
                  const isCurrentUserReceiver = trade.cashFlow?.receiver
                    ? trade.cashFlow.receiver === currentUser?.uid
                    : (trade.type === 'ask' && currentUser?.uid === trade.fromUserId) || (trade.type === 'offer' && currentUser?.uid === trade.toUserId);
                  const label = isCurrentUserReceiver
                    ? `+$${Number(trade.cashAmount).toLocaleString()} to you`
                    : `+$${Number(trade.cashAmount).toLocaleString()} sent`;
                  return <span>{label}</span>;
                })()}
              </div>
            ) : (
              <div className="tp-trade-type">
                Straight Trade
              </div>
            )}
          </div>
          {/* ToUser's Pedal (Right Column) */}
          <div className="tp-head-col" style={{ flex: '1 1 35%', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
            <Link
              to={`/archived-listing/trade/${trade.id}/${currentUser?.uid === trade.fromUserId ? 'their' : 'my'}`}
              className="tp-pedal-link"
            >
              <div className="tp-pedal-name-above" style={{ fontWeight: 600, fontSize: '12.5px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3', textAlign: 'left', display: 'none' }}>{theirPedal.make} {theirPedal.model}</div>
              <div className="tp-pedal-thumb">
                {(theirPedal?.images?.top || placeholderFor(theirPedal || {}).url) ? (
                  <img src={theirPedal?.images?.top || placeholderFor(theirPedal || {}).url} alt="ToUser's Pedal" />
                ) : (
                  <span style={{ fontSize: '10px', color: '#9ca3af' }}>No Image</span>
                )}
              </div>
              <div className="tp-pedal-text-beside" style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f2937', fontFamily: 'Nunito', lineHeight: '1.3' }}>{theirPedal.make} {theirPedal.model}</div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>${theirPedal.price}</div>
              </div>
              <div className="tp-pedal-price-below" style={{ fontSize: '12px', color: '#6b7280', display: 'none' }}>${theirPedal.price}</div>
            </Link>
            {trade && auth.currentUser && auth.currentUser.uid === trade.toUserId && trade.protection && trade.protection.toUser && trade.paymentStatus?.toUserPaid && (
              /* Shown only once this user's payment is COMPLETE (Jason,
                 2026-09-18): under the default-ON toggle the protection flag
                 alone means merely "selected", which predates any payment and
                 is irrelevant here. Inline styles on purpose — the previous
                 Tailwind centering classes are inert in this project, so the
                 badge sat left-aligned. */
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#1e40af', textAlign: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '12px' }}>Trade Protection Purchased</span>
              </div>
            )}
          </div>
        </div>

        {/* Cash Receive Info - inside the card */}
        {trade && (() => {
          let isCurrentUserReceiver = false;
          if (trade.cashFlow && trade.cashFlow.receiver) {
            isCurrentUserReceiver = trade.cashFlow.receiver === currentUser?.uid;
          } else {
            isCurrentUserReceiver = (trade.type === 'ask' && currentUser?.uid === trade.fromUserId) || 
                                   (trade.type === 'offer' && currentUser?.uid === trade.toUserId);
          }
          const payoutCents = getTradeReceiverPayoutCents(trade);
          if (isCurrentUserReceiver && payoutCents != null && payoutCents > 0) {
            const receiverGets = (payoutCents / 100).toFixed(2);
            return (
              <div style={{ 
                borderTop: '1px solid #e5e7eb',
                marginTop: '0.75rem',
                paddingTop: '0.5rem',
                fontSize: '13px', 
                color: '#374151',
                textAlign: 'center',
              }}>
                You receive: <span style={{ fontWeight: 'bold', color: '#10b981' }}>${receiverGets}</span>
                <span style={{ fontSize: '11px', color: '#6b7280' }}> (minus applied fees)</span>
              </div>
            );
          }
          return null;
        })()}
        </>
        )}

        <div className="tp-tl-divider" />

        <div className="tp-tl-topline">
          <span className="tp-tl-tradeid">Trade #{(id || trade.id || '').slice(-6).toUpperCase()}</span>
          <span className={`tp-badge tp-badge-${overallStatus.tone}`}>
            {overallStatus.tone === 'green' && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            )}
            {overallStatus.label}
          </span>
        </div>
        <div className="tp-tl-progress">
          <div style={{ width: `${milestoneTotal ? (milestoneComplete / milestoneTotal) * 100 : 0}%` }} />
        </div>
        <div className="tp-tl-progress-label">{milestoneComplete} of {milestoneTotal} milestones complete</div>

        {/* Messages + Report Issue */}
        <div className="tp-tl-headbox-actions">
          <Link to={`/trade-messages/${id}`} className="tp-tl-action-btn">
            Messages
          </Link>
          <button
            onClick={handleReportIssue}
            disabled={trade?.disputeHold === true}
            className="tp-tl-action-btn"
          >
            <span className={`tp-tl-action-dot${trade?.disputeHold ? ' disabled' : ''}`} />
            {trade?.disputeHold ? 'Issue reported' : 'Report an issue'}
          </button>
        </div>
        </div>
      </div>

      {/* Dispute Hold Banner */}
      {trade?.disputeHold && (
        <div style={{
          maxWidth: '768px',
          margin: '0 auto 1rem auto',
          padding: '0.75rem 1rem',
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <span style={{ fontSize: '1.25rem' }}>⚠️</span>
          <div>
            <p style={{ margin: 0, fontWeight: '600', color: '#92400e', fontSize: '0.875rem' }}>
              Dispute In Progress
            </p>
            <p style={{ margin: 0, color: '#92400e', fontSize: '0.75rem' }}>
              An issue has been reported for this trade. Escrow funds are on hold pending review by our support team.
            </p>
          </div>
        </div>
      )}

      {/* Payment Success Message */}
      {showPaymentSuccess && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          <div className="flex items-center">
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: '8px' }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
            <div>
              <p className="font-semibold">Payment Successful!</p>
              <p className="text-sm">
                {paymentSuccessType === 'cash_credit' 
                  ? 'Your payment has been completed using cash credit. Payment status updated below.'
                  : 'Your shipping label has been purchased and should appear below shortly.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="tp-timeline" style={{ maxWidth: '768px', width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        <style>{`
          .tp-timeline { padding: 0 0.5rem; font-family: 'Nunito', sans-serif; text-align: left; }
          .tp-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 9999px; font-size: 12px; font-weight: 700; white-space: nowrap; }
          .tp-badge-green { background: #dcfce7; color: #15803d; }
          .tp-badge-amber { background: #fdebc8; color: #b45309; }
          .tp-badge-rust { background: #f8e3d9; color: #ac4320; }
          .tp-badge-blue { background: #e0edff; color: #2f4fd4; }
          .tp-badge-gray { background: #eef1f5; color: #64748b; font-size: calc(12px + 1.5pt); }

          .tp-tl-topcard { background: #fff; border: 1px solid #eef0f2; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 18px; }
          .tp-tl-topline { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
          .tp-tl-tradeid { font-size: 0.95rem; font-weight: 600; color: #6b7280; }
          .tp-tl-users-row { display: flex; align-items: center; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
          .tp-tl-userchip { display: inline-flex; align-items: center; gap: 8px; font-weight: 700; color: #1f2937; font-size: 1rem; }
          .tp-tl-swap { color: #9ca3af; font-size: 1.2rem; }
          .tp-tl-avatar { width: 34px; height: 34px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; flex-shrink: 0; }
          .tp-tl-progress { height: 8px; background: #e5e7eb; border-radius: 9999px; overflow: hidden; margin-top: 14px; }
          .tp-tl-progress > div { height: 100%; background: #4db6ac; border-radius: 9999px; transition: width 0.3s ease; }
          .tp-tl-progress-label { font-size: 0.85rem; color: #6b7280; margin-top: 8px; }
          .tp-tl-headbox { background: #fff; border: 1px solid #eef0f2; border-radius: 16px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 18px; text-align: left; }
          .tp-tl-divider { height: 1px; background: #eef0f2; margin: 14px 0; }
          .tp-tl-headbox-actions { display: flex; flex-wrap: wrap; justify-content: center; align-items: stretch; gap: 0.75rem; margin-top: 14px; }

          .tp-tl-list { }
          .tp-tl-row { display: flex; gap: 14px; }
          .tp-tl-row:not(:first-child) .tp-tl-header { padding-top: 14px; }
          .tp-tl-rail { display: flex; flex-direction: column; align-items: center; width: 26px; flex-shrink: 0; }
          .tp-tl-dot { width: 26px; height: 26px; border-radius: 50%; border: 2px solid #d1d5db; background: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
          .tp-tl-dot.partial { border-color: #9fd6cf; }
          .tp-tl-dot.done { border-color: #4db6ac; background: #4db6ac; }
          .tp-tl-line { flex: 1; width: 2px; background: #d1d5db; margin: 3px 0; min-height: 16px; }
          .tp-tl-line.done { background: #4db6ac; }
          .tp-tl-content { flex: 1; min-width: 0; padding-bottom: 38px; }
          .tp-tl-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; padding: 6px 0; }
          .tp-tl-title { font-size: 1.3rem; font-weight: 800; color: #111827; line-height: 1.2; }
          .tp-tl-sub { font-size: 0.9rem; color: #6b7280; margin-top: 3px; }
          .tp-tl-chevron { color: #9ca3af; font-size: 0.9rem; transition: transform 0.2s ease; flex-shrink: 0; margin-top: 6px; }

          .tp-tl-panel { background: #fff; border: 1px solid #eef0f2; border-radius: 14px; padding: 14px; margin-top: 8px; margin-left: -40px; position: relative; z-index: 1; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
          .tp-tl-help { font-size: calc(0.82rem + 1pt); font-weight: 700; color: #6b7280; line-height: 1.45; margin-bottom: 12px; }
          .tp-tl-help-note { display: block; font-size: 0.82rem; font-weight: 400; margin-top: 4px; }
          .tp-tl-help .accent { color: #C9A227; }
          .tp-tl-users { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
          .tp-tl-user { background: transparent; border: none; border-radius: 0; padding: 4px 16px; min-width: 0; }
          .tp-tl-users > .tp-tl-user:first-child { border-right: 1px solid #eef0f2; padding-right: 14px; }
          .tp-tl-users > .tp-tl-user:last-child { padding-left: 14px; }
          .tp-tl-user-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; min-width: 0; }
          .tp-tl-user-name { font-weight: 700; color: #1f2937; font-size: 0.95rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .tp-tl-line-item { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; font-size: 0.85rem; color: #374151; margin-bottom: 10px; }
          .tp-tl-line-item .lbl { display: inline-flex; align-items: center; gap: 5px; color: #4b5563; }
          .tp-tl-link { color: #5A9ECB; font-size: calc(0.82rem + 1pt); font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
          .tp-tl-link:hover { text-decoration: underline; }
          .tp-tl-mono {
            font-size: 0.68rem;
            color: #9ca3af;
            margin-top: 6px;
            font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
            white-space: nowrap;
            overflow-x: auto;
            max-width: 100%;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .tp-tl-mono::-webkit-scrollbar { display: none; }
          .tp-tl-refresh { width: 100%; background: #fff; border: 1px solid #d1d5db; border-radius: 10px; padding: 10px 14px; font-family: 'Nunito', sans-serif; font-size: 0.95rem; font-weight: 600; color: #374151; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 12px; }
          .tp-tl-refresh:hover:not(:disabled) { background: #f9fafb; }
          .tp-tl-refresh:disabled { opacity: 0.5; cursor: not-allowed; }
          .tp-tl-report { background: none; border: none; color: #e57373; font-size: 0.72rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 0; }
          .tp-tl-photo-box { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 100%; }
          .tp-tl-photo-wrap { position: relative; display: inline-block; cursor: pointer; width: 100%; max-width: 160px; }
          .tp-tl-photo-wrap img { width: 100%; max-width: 160px; height: 110px; object-fit: cover; border-radius: 8px; display: block; }
          .tp-tl-photo-zoom {
            position: absolute;
            right: 6px;
            bottom: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #5F8FA0;
            pointer-events: none;
            filter: drop-shadow(0 1px 1px rgba(255,255,255,0.9));
          }
          .tp-tl-photo-zoom svg {
            width: 16px;
            height: 16px;
          }
          .tp-tl-photo-actions { display: flex; align-items: center; justify-content: center; gap: 14px; flex-wrap: wrap; }
          .tp-tl-gold-btn { background: #C9A227; border-radius: 9999px; border: none; padding: 10px 20px; font-family: 'Nunito', sans-serif; color: #fff; font-weight: 700; font-size: 0.95rem; cursor: pointer; text-align: center; line-height: 1.2; white-space: nowrap; display: inline-flex; align-items: center; justify-content: center; margin: 0; width: auto; max-width: 100%; }
          .tp-tl-gold-btn:disabled { opacity: 0.5; cursor: not-allowed; }
          a.tp-tl-gold-btn { text-decoration: none; color: #fff; }
          .tp-tl-gold-row { width: 100%; text-align: left; margin-top: 8px; margin-bottom: 10px; }
          .tp-tl-gold-row--mark-received { margin-top: 22px; }
          @media (max-width: 768px) {
            .tp-tl-users > .tp-tl-user:first-child { padding-right: 12px; padding-left: 0; }
            .tp-tl-users > .tp-tl-user:last-child { padding-left: 12px; padding-right: 0; }
            .tp-tl-user { padding: 4px 0; }
            .tp-tl-title { font-size: 1.15rem; }
            .tp-tl-user-name { font-size: 0.85rem; }
            .tp-tl-line-item { font-size: 0.8rem; }
            .tp-badge { padding: 2px 8px; font-size: 11px; }
            .tp-badge-gray { font-size: calc(11px + 1.5pt); }
          }
        `}</style>

        {/* Progress Steps */}
        <div className="tp-tl-list">
        {steps.map((step, idx) => {
  const aChecked = step.check(userA);
  const bChecked = step.check(userB);
  const isPhotoStep = step.label === 'Upload Confirmation Photo';
  const isPaymentStep = step.label === 'Completed Payment';
  const isReadyStep = step.label === 'Ready to Ship';
  const isLabelStep = step.label === 'Generate Shipping Label';
  const isTransitStep = step.label === 'Item In Transit';
  const isFeedbackStep = step.label === 'Feedback Provided';

  const stepCompleted = aChecked && bChecked;
  const hasContent = isPhotoStep || isReadyStep || isLabelStep || isTransitStep || isPaymentStep || isFeedbackStep;
  const isCollapsed = collapsedSteps[idx];
  const canToggle = stepCompleted && hasContent;
  const isLastStep = idx === steps.length - 1;

  // Payout row data (payment step). The cash receiver's card carries the row;
  // the amount is shown only to the receiver themselves and is the settled
  // net when available (getTradeReceiverPayoutCents), never the gross escrow.
  const tradeHasCash = Number(trade.cashAmount) > 0;
  const cashReceiverUid = trade.cashFlow?.receiver ||
    (trade.type === 'ask' ? trade.fromUserId : trade.toUserId);
  const payoutStatus = getPayoutReleaseStatus(
    trade.transferStatus,
    trade.transferStatus?.bothPedalsDeliveredAt
  );
  const payoutCents = getTradeReceiverPayoutCents(trade);
  const payoutAmountSuffix =
    currentUser?.uid === cashReceiverUid && payoutCents > 0
      ? `: $${(payoutCents / 100).toFixed(2)}`
      : '';

  return (
    <div key={idx} className="tp-tl-row">
      <div className="tp-tl-rail">
        <div className={`tp-tl-dot${stepCompleted ? ' done' : (aChecked || bChecked ? ' partial' : '')}`}>
          {stepCompleted && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          )}
        </div>
        {!isLastStep && <div className={`tp-tl-line${stepCompleted ? ' done' : ''}`} />}
      </div>
      <div className="tp-tl-content">
        <div
          className="tp-tl-header"
          onClick={() => canToggle && toggleStepCollapse(idx)}
          style={{ cursor: canToggle ? 'pointer' : 'default' }}
        >
          <div style={{ minWidth: 0 }}>
            <div className="tp-tl-title">{stepTitleMap[step.label] || step.label}</div>
            <div className="tp-tl-sub">{getStepSubtitle(step, aChecked, bChecked)}</div>
          </div>
          {canToggle && (
            <span className="tp-tl-chevron" style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)' }}>▾</span>
          )}
        </div>

      {/* Feedback Form Section - Only for Feedback Provided step */}
      {isFeedbackStep && !isCollapsed && (
        <div id="tp-feedback-section" className="tp-tl-panel">
          {trade && trade.status === 'complete' ? (
            <FeedbackForm 
              trade={trade} 
              currentUserId={currentUser?.uid} 
              onFeedbackSubmitted={fetchTrade}
              shareLinks={
                <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1.5rem'
                }}>
                  <button
                    onClick={handleShareToReddit}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#FF4500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Share on Reddit
                  </button>
                  <button
                    onClick={handleShareToFacebook}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      fontFamily: 'Nunito, sans-serif',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      color: '#1877F2',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Share on Facebook
                  </button>
                </div>
              }
            />
          ) : (
            <p style={{ color: '#6b7280' }}>Feedback will be available after delivery confirmation.</p>
          )}
        </div>
      )}

      {/* Photo Upload Section - Only for Upload Confirmation Photo step */}
      {isPhotoStep && !isCollapsed && (
        <div className="tp-tl-panel">
          {!stepCompleted && (
            <div className="tp-tl-help">
              Upload an image of the pedal being packaged, for verification purposes.
              <span className="tp-tl-help-note">Accepted formats: JPG, PNG, TIFF • Max size: 10MB.</span>
              <img
                className="confirmation-photo-example"
                src="/images/confirmation-photo-example.png"
                alt="Example: photograph your pedal packed in the open box with packing material"
                style={{ display: 'block', width: '260px', maxWidth: '100%', margin: '12px auto 0', opacity: 0.85, mixBlendMode: 'multiply' }}
              />
            </div>
          )}
          <div className="tp-tl-users">
            {/* User A card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('A')}>{getInitial(userAName)}</span>
                <span className="tp-tl-user-name">{userAName}</span>
              </div>
                            {trade.shippingStatus?.fromUserPhotos?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {trade.shippingStatus.fromUserPhotos.map((photoUrl, photoIdx) => (
                    <div key={photoIdx} className="tp-tl-photo-box">
                      <div
                        className="tp-tl-photo-wrap"
                        onClick={() => openConfirmationPhoto(trade.shippingStatus.fromUserPhotos, photoIdx, userAName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openConfirmationPhoto(trade.shippingStatus.fromUserPhotos, photoIdx, userAName); }}
                        aria-label="View confirmation photo full size"
                      >
                        <img
                          src={photoUrl}
                          alt={`${userAName}'s confirmation photo`}
                        />
                        <span className="tp-tl-photo-zoom" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <path d="M20 20l-3-3" />
                          </svg>
                        </span>
                      </div>
                      <div className="tp-tl-photo-actions">
                        <button
                          onClick={() => handleReportPhoto(photoUrl, trade.fromUserId, userAName)}
                          title="Report this photo"
                          className="tp-tl-report"
                        >
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#e57373', display: 'inline-block' }} />
                          Report
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="tp-badge tp-badge-gray">Photo pending</span>
              )}
{isFromUser && !userA.photoUploaded && isAddressComplete(shippingAddress) && (
                <div className="tp-tl-gold-row">
                  {!userA.paid && (
                    <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                  )}
                  <input
                    id="tp-userA-file"
                    type="file"
                    accept=".jpg,.jpeg,.png,.tiff,.svg,image/jpeg,image/png,image/tiff,image/svg+xml"
                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                    disabled={uploading || !userA.paid}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => document.getElementById('tp-userA-file').click()}
                    disabled={uploading || !userA.paid}
                    className="tp-tl-gold-btn"
                  >
                    Choose File
                  </button>
                  {uploading && (
                    <p className="text-sm text-blue-600 mt-2">Uploading...</p>
                  )}
                </div>
              )}
            </div>
            {/* User B card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('B')}>{getInitial(userBName)}</span>
                <span className="tp-tl-user-name">{userBName}</span>
              </div>
                            {trade.shippingStatus?.toUserPhotos?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {trade.shippingStatus.toUserPhotos.map((photoUrl, photoIdx) => (
                    <div key={photoIdx} className="tp-tl-photo-box">
                      <div
                        className="tp-tl-photo-wrap"
                        onClick={() => openConfirmationPhoto(trade.shippingStatus.toUserPhotos, photoIdx, userBName)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openConfirmationPhoto(trade.shippingStatus.toUserPhotos, photoIdx, userBName); }}
                        aria-label="View confirmation photo full size"
                      >
                        <img
                          src={photoUrl}
                          alt={`${userBName}'s confirmation photo`}
                        />
                        <span className="tp-tl-photo-zoom" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="7" />
                            <path d="M20 20l-3-3" />
                          </svg>
                        </span>
                      </div>
                      <div className="tp-tl-photo-actions">
                        <button
                          onClick={() => handleReportPhoto(photoUrl, trade.toUserId, userBName)}
                          title="Report this photo"
                          className="tp-tl-report"
                        >
                          <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#e57373', display: 'inline-block' }} />
                          Report
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="tp-badge tp-badge-gray">Photo pending</span>
              )}
{isToUser && !userB.photoUploaded && isAddressComplete(shippingAddress) && (
                <div className="tp-tl-gold-row">
                  {!userB.paid && (
                    <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                  )}
                  <input
                    id="tp-userB-file"
                    type="file"
                    accept=".jpg,.jpeg,.png,.tiff,.svg,image/jpeg,image/png,image/tiff,image/svg+xml"
                    onChange={(e) => handlePhotoUpload(e.target.files[0])}
                    disabled={uploading || !userB.paid}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => document.getElementById('tp-userB-file').click()}
                    disabled={uploading || !userB.paid}
                    className="tp-tl-gold-btn"
                  >
                    Choose File
                  </button>
                  {uploading && (
                    <p className="text-sm text-blue-600 mt-2">Uploading...</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Details Section - Only for Completed Payment step */}
      {isPaymentStep && !isCollapsed && (
        <div className="tp-tl-panel">
          {!stepCompleted && (
            <div className="tp-tl-help">
              Track payment completion and payout transfers for this trade.
            </div>
          )}
          {/* Escrow explainer — shared text above the user split, like every
              other section. Shown for the whole life of the hold (including
              after both payments complete, when the first help line hides),
              hidden only once released or when the trade moves no cash. */}
          {tradeHasCash && payoutStatus.state !== 'released' && (
            <div className="tp-tl-help" style={{ color: '#ac4320' }}>
              Cash is held in escrow and released 24 hours after both pedals are delivered.
            </div>
          )}
          <div className="tp-tl-users">
            {/* User A card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('A')}>{getInitial(userAName)}</span>
                <span className="tp-tl-user-name">{userAName}</span>
              </div>
              <div className="tp-tl-line-item">
                <span className="lbl">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  Payment
                </span>
                <span className={`tp-badge ${userA.paid ? 'tp-badge-green' : 'tp-badge-amber'}`}>{userA.paid ? 'Completed' : 'Pending'}</span>
              </div>
              {tradeHasCash && cashReceiverUid === trade.fromUserId && (
                <div className="tp-tl-line-item">
                  <span className="lbl">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    Payout{payoutAmountSuffix}
                  </span>
                  <PayoutReleaseBadge transferStatus={trade.transferStatus} deliveredAt={trade.transferStatus?.bothPedalsDeliveredAt} />
                </div>
              )}
              {isFromUser && !userA.paid && (
                <div className="tp-tl-gold-row">
                  <a href={`/trade-confirm/${trade.id}`} className="tp-tl-gold-btn">
                    Pay now
                  </a>
                </div>
              )}
            </div>
            {/* User B card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('B')}>{getInitial(userBName)}</span>
                <span className="tp-tl-user-name">{userBName}</span>
              </div>
              <div className="tp-tl-line-item">
                <span className="lbl">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  Payment
                </span>
                <span className={`tp-badge ${userB.paid ? 'tp-badge-green' : 'tp-badge-amber'}`}>{userB.paid ? 'Completed' : 'Pending'}</span>
              </div>
              {tradeHasCash && cashReceiverUid === trade.toUserId && (
                <div className="tp-tl-line-item">
                  <span className="lbl">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                    Payout{payoutAmountSuffix}
                  </span>
                  <PayoutReleaseBadge transferStatus={trade.transferStatus} deliveredAt={trade.transferStatus?.bothPedalsDeliveredAt} />
                </div>
              )}
              {isToUser && !userB.paid && (
                <div className="tp-tl-gold-row">
                  <a href={`/trade-confirm/${trade.id}`} className="tp-tl-gold-btn">
                    Pay now
                  </a>
                </div>
              )}
            </div>
          </div>

        </div>
      )}

      {/* Ready to Ship Section - Only for Ready to Ship step */}
      {isReadyStep && !isCollapsed && (
        <div className="tp-tl-panel">
          {!stepCompleted && (
            <div className="tp-tl-help">
              Confirm when your item is fully packed and you have the box dimensions and weight. Both traders must confirm before shipping labels become available. <span className="accent">We provide a discounted, fully insured label.</span>
            </div>
          )}
          <div className="tp-tl-users">
            {/* User A card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('A')}>{getInitial(userAName)}</span>
                <span className="tp-tl-user-name">{userAName}</span>
              </div>
              <div className="tp-tl-line-item">
                <span className="lbl">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-9"/><path d="M12 13 3 9l9-4 9 4-9 4z"/><path d="M3 9v10l9 4 9-4V9"/></svg>
                  Status
                </span>
                <span className={`tp-badge ${userA.ready ? 'tp-badge-green' : 'tp-badge-gray'}`}>{userA.ready ? 'Ready to ship' : 'Not ready yet'}</span>
              </div>
              {isFromUser && userA.photoUploaded && isAddressComplete(shippingAddress) && (
                <>
                  {!userA.paid && (
                    <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                  )}
                  <div className="tp-toggle-row">
                    <button
                      className={`tp-toggle${userA.ready ? ' active' : ''}${!userA.paid ? ' disabled' : ''}`}
                      onClick={() => userA.paid && handleReadyToShip(trade.fromUserId, !userA.ready)}
                      disabled={!userA.paid}
                    />
                    <span className="font-medium text-gray-700 tp-toggle-label">I'm ready to ship</span>
                  </div>
                </>
              )}
              {isToUser && (
                <div className="tp-toggle-row">
                  <span
                    className={`tp-toggle${userA.ready ? ' active' : ''} tp-toggle-readonly`}
                    aria-label={userA.ready ? 'Ready to ship' : 'Not ready yet'}
                  />
                  <span className="tp-toggle-readonly-label">{userA.ready ? 'Ready to ship' : 'Not ready yet'}</span>
                </div>
              )}
            </div>
            {/* User B card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('B')}>{getInitial(userBName)}</span>
                <span className="tp-tl-user-name">{userBName}</span>
              </div>
              <div className="tp-tl-line-item">
                <span className="lbl">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22v-9"/><path d="M12 13 3 9l9-4 9 4-9 4z"/><path d="M3 9v10l9 4 9-4V9"/></svg>
                  Status
                </span>
                <span className={`tp-badge ${userB.ready ? 'tp-badge-green' : 'tp-badge-gray'}`}>{userB.ready ? 'Ready to ship' : 'Not ready yet'}</span>
              </div>
              {isToUser && userB.photoUploaded && isAddressComplete(shippingAddress) && (
                <>
                  {!userB.paid && (
                    <p className="text-xs text-red-600 mb-2">⚠️ Complete payment first</p>
                  )}
                  <div className="tp-toggle-row">
                    <button
                      className={`tp-toggle${userB.ready ? ' active' : ''}${!userB.paid ? ' disabled' : ''}`}
                      onClick={() => userB.paid && handleReadyToShip(trade.toUserId, !userB.ready)}
                      disabled={!userB.paid}
                    />
                    <span className="font-medium text-gray-700 tp-toggle-label">I'm ready to ship</span>
                  </div>
                </>
              )}
              {isFromUser && (
                <div className="tp-toggle-row">
                  <span
                    className={`tp-toggle${userB.ready ? ' active' : ''} tp-toggle-readonly`}
                    aria-label={userB.ready ? 'Ready to ship' : 'Not ready yet'}
                  />
                  <span className="tp-toggle-readonly-label">{userB.ready ? 'Ready to ship' : 'Not ready yet'}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Generate Shipping Label Section - Only for Generate Shipping Label step */}
      {isLabelStep && !isCollapsed && (
        <div className="tp-tl-panel">
          {!stepCompleted && (
            <div className="tp-tl-help">
              Purchase your shipping label once both traders have marked their items as ready to ship.
              <ShippingPricesLink onClick={() => setShowShippingPrices(true)} style={{ display: 'block', marginTop: '10px' }} />
            </div>
          )}
          <div className="tp-tl-users">
            {/* User A card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('A')}>{getInitial(userAName)}</span>
                <span className="tp-tl-user-name">{userAName}</span>
              </div>
              {userA.label ? (
                <>
                  <div className="tp-tl-line-item">
                    <span className="lbl">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      Label
                    </span>
                    <span className="tp-badge tp-badge-green">Purchased</span>
                  </div>
                  {userA.labelUrl && isFromUser && (
                    <a
                      href={userA.labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { if (Capacitor.isNativePlatform()) { e.preventDefault(); openExternalUrl(userA.labelUrl); } }}
                      className="tp-tl-link"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download label
                    </a>
                  )}
                  {userA.tracking && (
                    <div className="tp-tl-mono">{userA.tracking}</div>
                  )}
                </>
              ) : (isFromUser && userA.ready && userB.ready && isAddressComplete(shippingAddress)) ? (
                <div className="tp-tl-gold-row">
                  <button onClick={handleOpenShippingForm} className="tp-tl-gold-btn">
                    Purchase Label
                  </button>
                </div>
              ) : (
                <span className="tp-badge tp-badge-gray">Not purchased</span>
              )}
            </div>
            {/* User B card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('B')}>{getInitial(userBName)}</span>
                <span className="tp-tl-user-name">{userBName}</span>
              </div>
              {userB.label ? (
                <>
                  <div className="tp-tl-line-item">
                    <span className="lbl">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      Label
                    </span>
                    <span className="tp-badge tp-badge-green">Purchased</span>
                  </div>
                  {userB.labelUrl && isToUser && (
                    <a
                      href={userB.labelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { if (Capacitor.isNativePlatform()) { e.preventDefault(); openExternalUrl(userB.labelUrl); } }}
                      className="tp-tl-link"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Download label
                    </a>
                  )}
                  {userB.tracking && (
                    <div className="tp-tl-mono">{userB.tracking}</div>
                  )}
                </>
              ) : (isToUser && userA.ready && userB.ready && isAddressComplete(shippingAddress)) ? (
                <div className="tp-tl-gold-row">
                  <button onClick={handleOpenShippingForm} className="tp-tl-gold-btn">
                    Purchase Label
                  </button>
                </div>
              ) : (
                <span className="tp-badge tp-badge-gray">Not purchased</span>
              )}
            </div>
          </div>

          {/* Shipping Label Purchase Modal - Only show within this step */}
          {showShippingForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={{ boxSizing: 'border-box' }}>
                <div className="p-6" style={{ overflowX: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '4px' }}>
                    <button 
                      onClick={handleCloseShippingForm}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb',
                        color: '#6b7280',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; e.currentTarget.style.color = '#374151'; e.currentTarget.style.borderColor = '#d1d5db'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Package Dimensions Form */}
                  <style>{`
                    .ship-form-input {
                      width: 100%;
                      box-sizing: border-box;
                      border-radius: 25px;
                      border: 1px solid #d1d5db;
                      padding: 8px 12px;
                      font-size: 16px;
                    }
                    .ship-readonly {
                      background-color: #f3f4f6;
                      color: #6b7280;
                      cursor: default;
                      border-color: #e5e7eb;
                    }
                    .ship-readonly:focus {
                      outline: none;
                      box-shadow: none;
                    }
                    .ship-dims-grid label,
                    .ship-dims-grid,
                    .ship-form-section {
                      text-align: left !important;
                    }
                    .ship-dims-grid {
                      display: grid;
                      grid-template-columns: 1fr 1fr;
                      gap: 16px;
                    }
                    .ship-city-row {
                      display: grid;
                      grid-template-columns: 1fr 1fr 1fr;
                      gap: 8px;
                    }
                    .ship-two-col {
                      display: grid;
                      grid-template-columns: 1fr 1fr;
                      gap: 8px;
                    }
                    @media (max-width: 480px) {
                      .ship-dims-grid {
                        grid-template-columns: 1fr;
                        gap: 8px;
                      }
                      .ship-city-row {
                        grid-template-columns: 1fr 1fr 1fr;
                      }
                    }
                  `}</style>
                  {shippingRates.length === 0 && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium mb-2">Package Dimensions</h4>
                        <div className="ship-dims-grid">
                          <div>
                            <label className="block text-sm font-medium mb-1">Length (inches)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={packageData.length}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers and one decimal point
                                if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                  setPackageData({...packageData, length: value});
                                }
                              }}
                              placeholder="0.0"
                              className="ship-form-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Width (inches)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={packageData.width}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers and one decimal point
                                if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                  setPackageData({...packageData, width: value});
                                }
                              }}
                              placeholder="0.0"
                              className="ship-form-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Height (inches)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={packageData.height}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers and one decimal point
                                if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                  setPackageData({...packageData, height: value});
                                }
                              }}
                              placeholder="0.0"
                              className="ship-form-input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Weight (pounds)</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={packageData.weight}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Only allow numbers and one decimal point
                                if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                  setPackageData({...packageData, weight: value});
                                }
                              }}
                              placeholder="0.0"
                              className="ship-form-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Simplified Address Form */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div>
                          <h4 className="font-medium mb-2">From Address</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              placeholder="Full Name"
                              value={packageData.fromAddress.name}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <input
                              type="text"
                              placeholder="Street Address"
                              value={packageData.fromAddress.street1}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <input
                              type="text"
                              placeholder="Apartment, suite, unit, etc. (optional)"
                              value={packageData.fromAddress.street2}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <div className="ship-city-row">
                              <input
                                type="text"
                                placeholder="City"
                                value={packageData.fromAddress.city}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="text"
                                placeholder="ST"
                                value={packageData.fromAddress.state}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="text"
                                placeholder="12345"
                                value={packageData.fromAddress.zip}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                            </div>
                            <div className="ship-two-col">
                              <input
                                type="email"
                                placeholder="Email (required for USPS)"
                                value={packageData.fromAddress.email}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="tel"
                                placeholder="Phone (required for USPS)"
                                value={packageData.fromAddress.phone}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium mb-2">To Address</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <input
                              type="text"
                              placeholder="Full Name"
                              value={packageData.toAddress.name}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <input
                              type="text"
                              placeholder="Street Address"
                              value={packageData.toAddress.street1}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <input
                              type="text"
                              placeholder="Apartment, suite, unit, etc. (optional)"
                              value={packageData.toAddress.street2}
                              readOnly
                              className="ship-form-input ship-readonly"
                            />
                            <div className="ship-city-row">
                              <input
                                type="text"
                                placeholder="City"
                                value={packageData.toAddress.city}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="text"
                                placeholder="ST"
                                value={packageData.toAddress.state}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="text"
                                placeholder="12345"
                                value={packageData.toAddress.zip}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                            </div>
                            <div className="ship-two-col">
                              <input
                                type="email"
                                placeholder="Email (optional)"
                                value={maskEmail(packageData.toAddress.email)}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                              <input
                                type="tel"
                                placeholder="Phone (optional)"
                                value={maskPhone(packageData.toAddress.phone)}
                                readOnly
                                className="ship-form-input ship-readonly"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={handleGetShippingRates}
                        disabled={loadingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                                  !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                                  !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                  !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                                  !packageData.toAddress.state || !packageData.toAddress.zip}
                        className={`transition-opacity ${
                          loadingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                          !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                          !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                          !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                          !packageData.toAddress.state || !packageData.toAddress.zip
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:opacity-80'
                        }`}
                        style={{
                          backgroundColor: (loadingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                                           !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                                           !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                           !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                                           !packageData.toAddress.state || !packageData.toAddress.zip) ? '#E5E7EB' : '#C9A227',
                          borderRadius: '9999px',
                          border: 'none',
                          padding: '6px 14px',
                          fontFamily: 'Nunito, sans-serif',
                          color: (loadingRates || !packageData.length || !packageData.width || !packageData.height || !packageData.weight || 
                                 !packageData.fromAddress.name || !packageData.fromAddress.street1 || !packageData.fromAddress.city || 
                                 !packageData.fromAddress.state || !packageData.fromAddress.zip || !packageData.fromAddress.email ||
                                 !packageData.toAddress.name || !packageData.toAddress.street1 || !packageData.toAddress.city || 
                                 !packageData.toAddress.state || !packageData.toAddress.zip) ? '#9CA3AF' : 'white',
                          fontWeight: 'bold',
                          fontSize: '15px',
                          marginTop: '12px'
                        }}
                      >
                        {loadingRates ? 'Getting Rates...' : 'Get Shipping Rates'}
                      </button>
                    </div>
                  )}

                  {/* Shipping Rates Display */}
                  {shippingRates.length > 0 && (
                    <div className="space-y-4">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
                        <h4 className="font-medium">Select Shipping Option</h4>
                        <ShippingPricesLink onClick={() => setShowShippingPrices(true)} style={{ fontSize: '15px' }} />
                      </div>
                      
                      {/* Group rates by carrier and sort by price */}
                      {(() => {
                        // Group rates by carrier
                        const ratesByCarrier = shippingRates.reduce((acc, rate) => {
                          const carrier = rate.provider;
                          if (!acc[carrier]) {
                            acc[carrier] = [];
                          }
                          acc[carrier].push(rate);
                          return acc;
                        }, {});

                        // Sort each carrier's rates by price (ascending)
                        Object.keys(ratesByCarrier).forEach(carrier => {
                          ratesByCarrier[carrier].sort((a, b) => parseFloat(a.displayAmount) - parseFloat(b.displayAmount));
                        });

                        // Get unique carriers
                        const carriers = Object.keys(ratesByCarrier);

                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
                            {carriers.map(carrier => (
                              <div key={carrier} style={{ 
                                flex: '1 1 300px', 
                                minWidth: '280px', 
                                maxWidth: '400px',
                                border: '2px solid #e5e7eb', 
                                borderRadius: '8px', 
                                padding: '16px', 
                                backgroundColor: '#ffffff',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                              }}>
                                <div style={{ 
                                  textAlign: 'center', 
                                  marginBottom: '16px', 
                                  paddingBottom: '12px', 
                                  borderBottom: '2px solid #e5e7eb'
                                }}>
                                  <h5 style={{ 
                                    fontWeight: 'bold', 
                                    fontSize: '20px', 
                                    color: '#1f2937',
                                    margin: '0'
                                  }}>{carrier}</h5>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                  {ratesByCarrier[carrier].map((rate, index) => (
                                    <div key={index} style={{ 
                                      border: '1px solid #d1d5db', 
                                      borderRadius: '8px', 
                                      padding: '12px', 
                                      backgroundColor: '#f9fafb'
                                    }}>
                                      <div style={{ marginBottom: '12px' }}>
                                        <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '4px' }}>
                                          {rate.servicelevel || 'Standard'}
                                        </div>
                                        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                          {rate.estimated_days} day{rate.estimated_days !== 1 ? 's' : ''} delivery
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Total Cost:</span>
                                          <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#059669' }}>${rate.displayAmount}</span>
                                        </div>
                                          <div style={{ 
                                          fontSize: '11px', 
                                          color: '#059669', 
                                          backgroundColor: '#f0fdf4', 
                                          padding: '6px 8px', 
                                          borderRadius: '4px',
                                          border: '1px solid #bbf7d0'
                                          }}>
                                          ✓ Includes insurance
                                          </div>
                                      </div>
                                      <button
                                        onClick={() => handleCreateCheckout(rate)}
                                        disabled={creatingCheckout}
                                        className={`transition-opacity ${
                                          creatingCheckout
                                            ? 'opacity-50 cursor-not-allowed'
                                            : 'hover:opacity-80'
                                        }`}
                                        style={{
                                          width: '100%',
                                          backgroundColor: creatingCheckout ? '#E5E7EB' : '#C9A227',
                                          color: creatingCheckout ? '#9CA3AF' : 'white',
                                          padding: '6px 14px',
                                          borderRadius: '9999px',
                                          border: 'none',
                                          fontWeight: 'bold',
                                          fontSize: '15px',
                                          fontFamily: 'Nunito, sans-serif',
                                          cursor: creatingCheckout ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {creatingCheckout ? 'Creating...' : 'Select This Rate'}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      
                      <button
                        onClick={() => setShippingRates([])}
                        className="hover:underline"
                        style={{ color: '#5A9ECB', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '16px', marginTop: '12px' }}
                      >
                        ← Back to Package Details
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Item In Transit Section - Only for Item In Transit step */}
      {isTransitStep && !isCollapsed && (
        <div className="tp-tl-panel">
          <button onClick={refreshTrackingStatus} disabled={refreshing} className="tp-tl-refresh">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            {refreshing ? 'Refreshing...' : 'Refresh tracking status'}
          </button>
          {/* Only once a package is actually moving; before that there is no
              button to press and the line would be a false promise. A
              partial shipment (one leg in transit) still earns it. */}
          {(userA.inTransit || userB.inTransit) && !(userA.delivered && userB.delivered) && trade.status !== 'complete' && (
            <div className="tp-tl-help">Tracking updates automatically. If your package arrives before the carrier reports it, mark it as received.</div>
          )}
          <div className="tp-tl-users">
            {/* User A card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('A')}>{getInitial(userAName)}</span>
                <span className="tp-tl-user-name">{userAName}</span>
              </div>
              {userA.tracking ? (
                <>
                  <div className="tp-tl-line-item">
                    <span className="lbl">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      Status
                    </span>
                    <span className={`tp-badge ${userA.delivered ? 'tp-badge-green' : userA.inTransit ? 'tp-badge-blue' : 'tp-badge-gray'}`}>{userA.delivered ? 'Delivered' : userA.inTransit ? 'In transit' : 'Label created'}</span>
                  </div>
                  <div className="tp-tl-mono">{userA.tracking}</div>
                  {userA.lastTrackingUpdate && (
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>Last updated {new Date(userA.lastTrackingUpdate.seconds * 1000).toLocaleDateString()}</div>
                  )}
                  {userA.trackingHistory.filter(event => typeof event.status === 'string').length > 0 && (
                    <details style={{ fontSize: '12px', marginTop: '8px' }}>
                      <summary className="tp-tl-link" style={{ cursor: 'pointer' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        View history ({userA.trackingHistory.filter(event => typeof event.status === 'string').length})
                      </summary>
                      <div style={{ marginTop: '8px', maxHeight: '256px', overflowY: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '6px', maxWidth: '100%' }}>
                        {userA.trackingHistory.filter(event => typeof event.status === 'string').map((event, hIdx) => (
                          <div key={hIdx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                            <div style={{ fontWeight: '700', fontSize: '10px', color: '#1f2937', textTransform: 'uppercase' }}>{event.status || 'Unknown'}</div>
                            {(event.statusDetail || event.status_details) && (<div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>{event.statusDetail || event.status_details}</div>)}
                            {event.location && (<div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>📍 {event.location}</div>)}
                            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>{event.statusDate ? new Date(event.statusDate).toLocaleDateString() : event.status_date ? new Date(event.status_date).toLocaleDateString() : ''}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <span className="tp-badge tp-badge-gray">No tracking</span>
              )}
              {/* In Transit panel. The separate "Awaiting delivery" step was
      removed 2026-11-01 (owner): it repeated the Delivered badge shown here and
      only added this button, so the button lives with the tracking it confirms.
      Only the RECEIVER of a package may mark it received, and only once the
      carrier has reported it in transit. The write is unchanged
      (shippingStatus.<side>.delivered), so the server-side completion, escrow
      timer, feedback and congratulations flow all run exactly as before. */}
              {userA.delivered && userA.deliveredAt && (
                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>
                  Delivered {new Date(userA.deliveredAt.seconds ? userA.deliveredAt.seconds * 1000 : userA.deliveredAt).toLocaleDateString()}
                </div>
              )}
              {isToUser && userA.inTransit && !userA.delivered && trade.status !== 'complete' && (
                <div className="tp-tl-gold-row tp-tl-gold-row--mark-received">
                  <button
                    onClick={() => handleManualDeliveryConfirmation('fromUser')}
                    disabled={markingReceived === 'fromUser'}
                    className="tp-tl-gold-btn"
                    style={{ opacity: markingReceived === 'fromUser' ? 0.6 : 1 }}
                  >
                    {markingReceived === 'fromUser' ? 'Marking Received...' : 'Mark as Received'}
                  </button>
                </div>
              )}
            </div>
            {/* User B card */}
            <div className="tp-tl-user">
              <div className="tp-tl-user-head">
                <span className="tp-tl-avatar" style={getAvatarStyle('B')}>{getInitial(userBName)}</span>
                <span className="tp-tl-user-name">{userBName}</span>
              </div>
              {userB.tracking ? (
                <>
                  <div className="tp-tl-line-item">
                    <span className="lbl">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      Status
                    </span>
                    <span className={`tp-badge ${userB.delivered ? 'tp-badge-green' : userB.inTransit ? 'tp-badge-blue' : 'tp-badge-gray'}`}>{userB.delivered ? 'Delivered' : userB.inTransit ? 'In transit' : 'Label created'}</span>
                  </div>
                  <div className="tp-tl-mono">{userB.tracking}</div>
                  {userB.lastTrackingUpdate && (
                    <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>Last updated {new Date(userB.lastTrackingUpdate.seconds * 1000).toLocaleDateString()}</div>
                  )}
                  {userB.trackingHistory.filter(event => typeof event.status === 'string').length > 0 && (
                    <details style={{ fontSize: '12px', marginTop: '8px' }}>
                      <summary className="tp-tl-link" style={{ cursor: 'pointer' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        View history ({userB.trackingHistory.filter(event => typeof event.status === 'string').length})
                      </summary>
                      <div style={{ marginTop: '8px', maxHeight: '256px', overflowY: 'auto', backgroundColor: '#f9fafb', padding: '8px', borderRadius: '6px', maxWidth: '100%' }}>
                        {userB.trackingHistory.filter(event => typeof event.status === 'string').map((event, hIdx) => (
                          <div key={hIdx} style={{ marginBottom: '8px', padding: '8px', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                            <div style={{ fontWeight: '700', fontSize: '10px', color: '#1f2937', textTransform: 'uppercase' }}>{event.status || 'Unknown'}</div>
                            {(event.statusDetail || event.status_details) && (<div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px' }}>{event.statusDetail || event.status_details}</div>)}
                            {event.location && (<div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>📍 {event.location}</div>)}
                            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>{event.statusDate ? new Date(event.statusDate).toLocaleDateString() : event.status_date ? new Date(event.status_date).toLocaleDateString() : ''}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <span className="tp-badge tp-badge-gray">No tracking</span>
              )}
              {/* See the matching note on the other card. */}
              {userB.delivered && userB.deliveredAt && (
                <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '6px' }}>
                  Delivered {new Date(userB.deliveredAt.seconds ? userB.deliveredAt.seconds * 1000 : userB.deliveredAt).toLocaleDateString()}
                </div>
              )}
              {isFromUser && userB.inTransit && !userB.delivered && trade.status !== 'complete' && (
                <div className="tp-tl-gold-row tp-tl-gold-row--mark-received">
                  <button
                    onClick={() => handleManualDeliveryConfirmation('toUser')}
                    disabled={markingReceived === 'toUser'}
                    className="tp-tl-gold-btn"
                    style={{ opacity: markingReceived === 'toUser' ? 0.6 : 1 }}
                  >
                    {markingReceived === 'toUser' ? 'Marking Received...' : 'Mark as Received'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
})}
      </div>
      </div>

      {/* Enhanced Ready to Ship UI - Moved to the step sections above */}

      <ImageOverlay
        isOpen={photoOverlay.isOpen}
        imageUrl={photoOverlay.url}
        imageAlt={photoOverlay.alt}
        onClose={closeConfirmationPhoto}
        onNext={showNextConfirmationPhoto}
        onPrev={showPrevConfirmationPhoto}
        hasNext={photoOverlay.index < photoOverlay.photos.length - 1}
        hasPrev={photoOverlay.index > 0}
      />

      {/* Footer */}
      <div className="py-4" style={{ paddingBottom: '10px', marginTop: 'auto', backgroundColor: '#F7F3EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button
                type="button"
                onClick={() => openContact({ category: 'general' })}
                className="hover:underline"
                style={{ color: '#5A9ECB', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Inline shipping-address modal — auto-opens when the user has no
          complete address on file (needed to progress in the trade). */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="shipping"
      />

      <ShippingPricesModal
        isOpen={showShippingPrices}
        onClose={() => setShowShippingPrices(false)}
        variant="trade"
      />
    </div>
  );
};

export default TradeProgress;
